import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { todayIsoInZone, DEAL_SORT_OPTIONS, sortDeals, filterDeals, isLevelUnit } from '@harvus/shared';
import { B } from '../constants/brand';
import { DETAIL_TYPE_OPTIONS, DETAIL_TYPE_COLOR } from '../constants/detailTypes';
import { fmt } from '../utils/format';
import { groupSageTransactions } from '@harvus/shared';
import DealSageEntitiesStrip from '../components/shared/DealSageEntitiesStrip';
import { api } from '../api/client';
import { usePublishQuickNoteContext } from '../notebook/quickNoteContext';
import { useApi } from '../hooks/useApi';
import { Btn, Input, Textarea, Select, Modal, Card, Table, Tabs, Spinner, ErrorMsg, InfoGrid, Badge, StatusBadge, useToast, Icon, PageHeader, clickable } from '../components/ui';
import { DocumentsPanel } from '../components/shared/DocumentsPanel';
import { HarvusLeadMultiSelect } from '../components/shared/DealPickers';
import { DealContactsTab } from '../components/shared/DealTabs';
import { DealResearchTab } from '../components/shared/DealResearchTab';
import { ChatPanel } from '../components/shared/ChatPanel';
import { BulkResearchModal } from '../components/shared/BulkResearchModal';
import { NaicsBulkModal } from '../components/shared/NaicsBulkModal';
import { NaicsField } from '../components/shared/NaicsField';
import { DealNaicsChip } from '../components/shared/DealNaicsChip';
import { NotesTasksPanel } from '../components/shared/NotesTasksPanel';
import { DealProjectsPanel } from '../components/shared/DealProjectsPanel';
import { ParcelsMap } from '../components/shared/ParcelsMapTab';
import { ParcelsList } from '../components/shared/ParcelsList';
import { ProspectParcelPicker } from '../components/shared/ProspectParcelPicker';
import { RealEstateBudgetTab } from '../components/shared/RealEstateBudgetTab';
import { CapitalScheduleTab } from '../components/shared/CapitalScheduleTab';
import { ValuationTab } from '../components/shared/ValuationTab';
import { SageJournalTab } from '../components/shared/SageJournalTab';
import { exportTransactionsXlsx } from '../utils/exportDownload';

const DEAL_STATUS_OPTIONS   = ['', 'Prospect', 'Under Contract/LOI', 'Active', 'Trouble', "Exit'd", 'Dead'];
// Prospect pipeline stages (mirrors the API's PROSPECT_STATUSES).
const PROSPECT_STATUS_OPTIONS = ['Likely', 'Unlikely', 'Contract/LOI', 'Closed/Sold', 'Dead'];
const PROSPECT_STATUS_COLOR   = { Likely: B.green, 'Contract/LOI': B.tealDk, 'Closed/Sold': B.navy, Unlikely: B.amber, Dead: B.red };

// Enter any two of (acres, salesPrice, pricePerAcre) and fill in the third —
// acres × pricePerAcre = salesPrice. `edited` is the field the user just
// changed; the already-populated companion is treated as the anchor.
const trimNum = (n) => {
  if (n == null || !Number.isFinite(n)) return '';
  return String(Math.round(n * 10000) / 10000);
};
function recalcProspectEconomics(edited, form) {
  const acres = parseFloat(form.acres);
  const sales = parseFloat(form.salesPrice);
  const ppa   = parseFloat(form.pricePerAcre);
  const has   = (v) => Number.isFinite(v);
  const next  = { acres: form.acres, salesPrice: form.salesPrice, pricePerAcre: form.pricePerAcre };
  if (edited === 'acres' && has(acres)) {
    if (has(ppa))        next.salesPrice   = trimNum(acres * ppa);
    else if (has(sales) && acres !== 0) next.pricePerAcre = trimNum(sales / acres);
  } else if (edited === 'salesPrice' && has(sales)) {
    if (has(acres) && acres !== 0) next.pricePerAcre = trimNum(sales / acres);
    else if (has(ppa) && ppa !== 0) next.acres        = trimNum(sales / ppa);
  } else if (edited === 'pricePerAcre' && has(ppa)) {
    if (has(acres))       next.salesPrice = trimNum(acres * ppa);
    else if (has(sales) && ppa !== 0) next.acres      = trimNum(sales / ppa);
  }
  return next;
}
const UPDATE_FREQUENCY_OPTIONS = ['', 'Annually', 'Quarterly', 'Monthly', 'Weekly', 'Daily'];

const DETAIL_STATUS_OPTIONS = ['', 'Prospect', 'Under Contract/LOI', 'Active', 'Trouble', "Exit'd", 'Dead'];

// The four capital types, their words and their colour, now live in
// constants/detailTypes.js — the Cash Flow Projection report badges the same
// four types on its own rows, and a second copy of a colour map is a pair that
// drifts.

// Deal-list sorting + filtering now lives in @harvus/shared (DEAL_SORT_OPTIONS /
// sortDeals / filterDeals) so the web and mobile clients stay in lockstep.

const PROPERTY_TYPE_OPTIONS = [
  { value: '', label: '— Select type —' },
  { value: 'Recreational/Ag', label: 'Recreational/Ag' },
  { value: 'Commercial', label: 'Commercial' },
  { value: 'Residential', label: 'Residential' },
  { value: 'Industrial', label: 'Industrial' },
];

const HOLD_TYPE_OPTIONS = [
  { value: '', label: '— Select hold type —' },
  { value: 'Investment (L-T)', label: 'Investment (L-T)' },
  { value: 'Available for Sale (AFS)', label: 'Available for Sale (AFS)' },
  // Held for the owner's own use — no sale pipeline, no return target. The
  // detail card renders it through the generic (neither AFS nor L-T) branch.
  { value: 'Personal Use', label: 'Personal Use' },
];

// ---------------------------------------------------------------------------
// DealDetail modal
// ---------------------------------------------------------------------------
const TENANT_STATUS_OPTIONS = [
  { value: 'Active',   label: 'Active'   },
  { value: 'Expired',  label: 'Expired'  },
  { value: 'Upcoming', label: 'Upcoming' },
];

const RATE_TYPE_OPTIONS    = [{ value: 'Fixed', label: 'Fixed Rate' }, { value: 'Variable', label: 'Variable Rate' }];
const PAYMENT_TYPE_OPTIONS = ['Monthly', 'Quarterly', 'SemiAnnual', 'Annual', 'Maturity'].map(v => ({ value: v, label: v }));
const CONVENTION_OPTIONS   = [
  { value: 'LoanDate',    label: 'Loan Date — same day the loan was made' },
  { value: 'CalendarEnd', label: 'Calendar End — last day of the period' },
  { value: 'FirstDay',    label: 'First Day — first day of the period' },
  { value: 'Other',       label: 'Other — specify a day of the month' },
];
const DAY_COUNT_OPTIONS = [
  { value: '30/360',       label: '30/360 — 30 days/month, 360-day year (default)' },
  { value: 'Actual/360',   label: 'Actual/360 — actual days / 360' },
  { value: 'Actual/Actual', label: 'Actual/Actual — actual days / actual year days' },
];
const AMORTIZATION_TYPE_OPTIONS = [
  { value: 'InterestOnly',       label: 'Interest Only' },
  { value: 'PMT',                label: 'PMT — equal amortizing payment (Excel PMT)' },
  { value: 'ScheduledReduction', label: 'Scheduled Principal Reduction' },
];

// ---------------------------------------------------------------------------
// TransactionsExportButton — the deal-detail ledger as a .xlsx.
//
// THE FILE IS DELIBERATELY NOT THE PAGE ABOVE IT. The tab shows the 200 most
// recent postings; the export covers the whole ledger, grouped and subtotalled
// the same way. That difference is the reason the button exists, so the button
// says it — otherwise a file with more rows than the screen reads as a bug.
//
// The result is reported, not assumed: a run that hits the server's own cap
// comes back `truncated`, and a spreadsheet that stops early with nothing
// saying so is exactly the failure this endpoint was written to avoid.
// ---------------------------------------------------------------------------
const TransactionsExportButton = ({ dealId, dealDetailId }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const res = await exportTransactionsXlsx(dealId, dealDetailId);
      const rows = res?.rowCount ?? 0;
      const ents = (res?.entitySheets || []).length;
      const entNote = ents ? ` Plus ${ents} Sage entity sheet${ents === 1 ? '' : 's'}.` : '';
      toast.success((res?.truncated
        ? `Exported the first ${rows.toLocaleString()} postings (the ${res.maxRows.toLocaleString()}-row export cap).`
        : `Exported ${rows.toLocaleString()} posting${rows === 1 ? '' : 's'}.`) + entNote);
    } catch (e) {
      toast.error(e.message || 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Btn size="sm" variant="secondary" onClick={run} disabled={busy} loading={busy}
         title="Every posting on this deal detail, not just the ones shown below — plus one sheet per Sage entity attached to the deal">
      ⬇ Export to Excel
    </Btn>
  );
};

// ---------------------------------------------------------------------------
// SageTransactionGroups — one ledger, grouped by account class with a subtotal
// each. The Transactions tab renders it twice: for the capital item's own
// postings and once per Sage entity attached to the deal. One component, so
// the two cannot come to render the same posting two different ways.
//
// EVERY GROUP STARTS COLLAPSED, AND A COLLAPSED GROUP STILL SHOWS ITS TOTAL.
// The question this tab is opened to answer is "what is in Assets, what is in
// Debt" — the class subtotals — and a hundred asset postings pushed the Debt
// heading a screen and a half down. So the header row (label · posting count
// · subtotal) is always visible and is the toggle; the rows beneath it are
// what expands. Expanded state is per group and per mount — it is a reading
// preference, not a fact about the ledger, so nothing is stored.
// ---------------------------------------------------------------------------
const SageTransactionGroups = ({ rows, truncated, limit }) => {
  const groups = useMemo(() => groupSageTransactions(rows), [rows]);
  // Keyed by group key so the same class stays open across a re-render that
  // changes the row set (a refresh after an import). A key absent from the
  // map is collapsed — that is the default, so a group new to the list also
  // arrives collapsed rather than open.
  const [open, setOpen] = useState({});
  const toggle = key => setOpen(prev => ({ ...prev, [key]: !prev[key] }));
  const allOpen = groups.length > 0 && groups.every(g => open[g.key]);
  const setAll = value => setOpen(Object.fromEntries(groups.map(g => [g.key, value])));

  return (
    <>
      {/* A SUBTOTAL OVER A TRUNCATED LIST READS AS A CLASS BALANCE AND IS NOT
          ONE. The cap is on the most recent postings, so the groups below can
          be short by however much was cut off — say so rather than printing a
          number that invites being trusted. */}
      {truncated && (
        <div style={{ marginBottom: 12, fontSize: 12, color: B.textMd }}>
          Showing the {limit || 200} most recent postings — the group
          totals below cover only these rows, not the account's full balance.
        </div>
      )}
      {groups.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <button
            type="button"
            onClick={() => setAll(!allOpen)}
            style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer',
                     fontSize: 12, color: B.textMd, textDecoration: 'underline' }}
          >
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
      )}
      {groups.map(g => {
        const isOpen = !!open[g.key];
        return (
          <div key={g.key} style={{ marginBottom: isOpen ? 20 : 8 }}>
            <button
              type="button"
              onClick={() => toggle(g.key)}
              aria-expanded={isOpen}
              title={isOpen ? 'Hide postings' : 'Show postings'}
              style={{
                display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 12, padding: '6px 4px', background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left', borderBottom: `1px solid ${B.border}`, marginBottom: isOpen ? 6 : 0,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: B.textMd }}>
                <span aria-hidden="true" style={{ display: 'inline-block', width: 14, color: B.textLt, fontSize: 10 }}>
                  {isOpen ? '▼' : '▶'}
                </span>
                {g.label}
                <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: B.textLt }}>
                  {' '}· {g.rows.length} {g.rows.length === 1 ? 'posting' : 'postings'}
                </span>
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: B.navy }}>{fmt.currency(g.total)}</span>
            </button>
            {isOpen && (
              <Table
                columns={[
                  { key: 'transactionDate', label: 'Date',        render: v => fmt.date(v) },
                  { key: 'accountNumber',   label: 'Account',     render: v => <span style={{ color: B.textMd }}>{v || '—'}</span> },
                  { key: 'description',     label: 'Description', render: v => <span style={{ color: B.text }}>{v || '—'}</span> },
                  { key: 'amount',          label: 'Amount',      render: v => v != null ? <span style={{ color: B.navy }}>{fmt.currency(v)}</span> : <span style={{ color: B.textLt }}>—</span> },
                ]}
                data={g.rows}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

const SegSectionDivider = ({ label }) => (
  <div style={{ borderTop: `1px solid ${B.border}`, marginTop: 4, paddingTop: 14, marginBottom: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: B.textLt }}>
      {label}
    </div>
  </div>
);
const SegHelperText = ({ children }) => (
  <div style={{ fontSize: 12, color: B.textLt, marginTop: -12, marginBottom: 12, lineHeight: 1.4 }}>
    {children}
  </div>
);
const SegMutualNote = () => (
  <div style={{ fontSize: 12, color: B.amber, background: B.amber + '14', border: `1px solid ${B.amber}33`,
    borderRadius: 6, padding: '7px 10px', marginTop: -8, marginBottom: 12, lineHeight: 1.4 }}>
    Enter either a dollar amount or a percentage — not both. Filling one will clear the other.
  </div>
);

// includeDealLevelNotes: fold deal-level notes/tasks (DealDetailID IS NULL) into
// this detail's Notes & Tasks panel (summary + Tasks + Notes lists). True only for
// the single-detail unified view, where the detail view is the deal's ONLY home for
// deal-level notes. On a 2+ detail deal, deal-level notes/tasks live in the separate
// "Deal-Level Notes & Docs" section, so a specific detail shows ONLY its own.
const DetailModal = ({ deal, detail, users, sageLocations, sageAccounts, rateIndexes, user, onDealChanged, onClose, onUpdated, inline = false, unifiedMode = false, includeDealLevelNotes = false, targetNoteId = null, onNoteTargetHandled }) => {
  const { toast } = useToast();
  const [activeTab,  setActiveTab]  = useState('Notes & Tasks');
  // The Accounting tab groups the deal's Sage entities with the R/E accounting
  // views (Valuation, Budget, Sage JE) behind a second row of tabs.
  const [accountingTab, setAccountingTab] = useState('Sage Entities');
  // mapFocus: when the Map tab's map is clicked it enlarges and the detail info
  // above the tabs is hidden to give it room.
  const [mapFocus,   setMapFocus]   = useState(false);
  const [showEdit,   setShowEdit]   = useState(false);
  const [editForm,   setEditForm]   = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  // unifiedMode: show deal-level + this detail's docs together in one panel.
  // Filter deal.documents to DealDetailID=null OR DealDetailID=this detail.
  const [detailDocs, setDetailDocs] = useState(
    unifiedMode
      ? (deal.documents || []).filter(d => !d.dealDetailID || d.dealDetailID === detail.dealDetailID)
      : (detail.documents || [])
  );
  const setE = (k, v) => setEditForm(p => ({ ...p, [k]: v }));

  // R/E tenants (local state so edits reflect immediately)
  const [tenants,      setTenants]      = useState(detail.rentSchedule || []);
  const [tenantModal,  setTenantModal]  = useState(null); // null | 'add' | row
  const [tenantForm,   setTenantForm]   = useState({});
  const [tenantSaving, setTenantSaving] = useState(false);
  const setTF = (k, v) => setTenantForm(p => ({ ...p, [k]: v }));

  // N/R payment terms (loan schedule segments)
  const [segments,  setSegments]  = useState(detail.segments || []);
  const [segModal,  setSegModal]  = useState(null); // null | 'add' | row
  const [segForm,   setSegForm]   = useState({});
  const [segSaving, setSegSaving] = useState(false);
  const setSF = (k, v) => setSegForm(p => ({ ...p, [k]: v }));

  // N/R payment generation (backfill)
  const [genModal,  setGenModal]  = useState(false);
  const [genForm,   setGenForm]   = useState({});
  const [genSaving, setGenSaving] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const setGF = (k, v) => setGenForm(p => ({ ...p, [k]: v }));

  const openGenModal = () => {
    setGenForm({
      // `loanTerms` below, not detail.loanTerms — it carries the pre-deploy
      // fallback, so an older API payload still pre-fills the window from the
      // typed columns instead of opening blank. Read on click, long after the
      // const is initialized.
      fromDate: loanTerms.loanDate || '',
      toDate:   loanTerms.maturityDate || '',
    });
    setGenResult(null);
    setGenModal(true);
  };

  const submitGenPayments = async () => {
    if (!genForm.fromDate || !genForm.toDate) return;
    setGenSaving(true);
    try {
      const res = await api.post(`deals/${deal.dealID}/details/${detail.dealDetailID}/payments/generate`, {
        fromDate: genForm.fromDate,
        toDate:   genForm.toDate,
      });
      setGenResult(res);
      onUpdated();
    } finally {
      setGenSaving(false);
    }
  };

  // R/E prospects
  const EMPTY_PROSPECT_FORM = { customer: '', salesPrice: '', acres: '', pricePerAcre: '', status: 'Likely', closingDate: '', notes: '', parcelIds: [] };
  const [prospects,        setProspects]        = useState(detail.prospects || []);
  const [showProspectForm, setShowProspectForm] = useState(false);
  const [editingProspect,  setEditingProspect]  = useState(null); // prospectID being edited, or null for new
  const [prospectForm,     setProspectForm]     = useState(EMPTY_PROSPECT_FORM);
  const [prospectSaving,   setProspectSaving]   = useState(false);
  const setProF = (k, v) => setProspectForm(p => ({ ...p, [k]: v }));
  // Economics field change → recompute the third value from the other two.
  const setProEcon = (k, v) => setProspectForm(p => recalcProspectEconomics(k, { ...p, [k]: v }));

  const openEdit = () => {
    const leadIds = Array.isArray(detail.harvusLeadUserIDs)
      ? detail.harvusLeadUserIDs
      : (detail.harvusLeadUserIDs || '').split(',').filter(Boolean);
    setEditForm({
      detailType:         detail.detailType         || 'N/R',
      status:             detail.status             || '',
      sageLocationId:     detail.sageLocationID     || '',
      sageAccountId:      detail.sageAccountID      || '',
      amortizationMonths: detail.amortizationMonths != null ? String(detail.amortizationMonths) : '',
      propertyType:       detail.propertyType       || '',
      holdType:           detail.holdType           || '',
      budgetedCost:       detail.budgetedCost       != null ? String(detail.budgetedCost)       : '',
      budgetedSalesPrice: detail.budgetedSalesPrice != null ? String(detail.budgetedSalesPrice) : '',
      estimatedExit:      detail.estimatedExit      ? detail.estimatedExit.slice(0, 10)         : '',
      purchaseDate:       detail.purchaseDate       ? detail.purchaseDate.slice(0, 10)          : '',
      committedCapital:   detail.committedCapital   != null ? String(detail.committedCapital)   : '',
      harvusLeadUserIDs:  leadIds,
      description:        detail.description        || '',
      estimatedIRR:       detail.estimatedIRR       != null ? String(detail.estimatedIRR * 100) : '',
      actualIRR:          detail.actualIRR           != null ? String(detail.actualIRR    * 100) : '',
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.put(`deals/${deal.dealID}/details/${detail.dealDetailID}`, {
        detailType:         editForm.detailType         || null,
        status:             editForm.status             || null,
        sageLocationId:     editForm.sageLocationId     || null,
        sageAccountId:      editForm.sageAccountId      || null,
        // Sent as null when blank, which the API reads as an explicit CLEAR
        // rather than as "unchanged" — every sibling field here is COALESCE'd
        // and cannot be emptied, and this one has to be.
        amortizationMonths: editForm.amortizationMonths !== '' ? parseInt(editForm.amortizationMonths, 10) : null,
        propertyType:       editForm.propertyType        || null,
        holdType:           editForm.holdType            || null,
        budgetedCost:       editForm.budgetedCost        !== '' ? parseFloat(editForm.budgetedCost)       : null,
        budgetedSalesPrice: editForm.budgetedSalesPrice  !== '' ? parseFloat(editForm.budgetedSalesPrice) : null,
        estimatedExit:      editForm.estimatedExit       || null,
        purchaseDate:       editForm.purchaseDate        || null,
        committedCapital:   editForm.committedCapital     !== '' ? parseFloat(editForm.committedCapital)   : null,
        harvusLeadUserIDs:  editForm.harvusLeadUserIDs   || [],
        description:        editForm.description        || null,
        estimatedIRR:       editForm.estimatedIRR !== '' ? parseFloat(editForm.estimatedIRR) / 100 : null,
        actualIRR:          editForm.actualIRR    !== '' ? parseFloat(editForm.actualIRR)    / 100 : null,
      });
      setShowEdit(false);
      if (onUpdated) onUpdated();
    } catch (e) { toast.error(e.message); }
    finally { setEditSaving(false); }
  };

  const deleteDetail = async () => {
    if (!window.confirm(`Delete this ${detail.detailType} detail? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.del(`deals/${deal.dealID}/details/${detail.dealDetailID}`);
      if (!inline && onClose) onClose();
      if (onUpdated) onUpdated();
    } catch (e) { toast.error(e.message); }
    finally { setDeleting(false); }
  };

  const refreshDocs = async () => {
    try {
      if (unifiedMode) {
        // Fetch the full deal to get all docs, then filter to deal-level + this detail
        const d = await api.get(`deals/${deal.dealID}`);
        setDetailDocs((d.documents || []).filter(doc => !doc.dealDetailID || doc.dealDetailID === detail.dealDetailID));
      } else {
        const d = await api.get(`deals/${deal.dealID}/details/${detail.dealDetailID}`);
        setDetailDocs(d.documents || []);
      }
    } catch {}
  };

  // Tenant CRUD
  const openAddTenant = () => {
    setTenantForm({ tenantName: '', monthlyRent: '', leaseStartDate: '', leaseEndDate: '', status: 'Active', paymentDueDay: '', reminderDaysBefore: '30' });
    setTenantModal('add');
  };
  const openEditTenant = (row) => {
    setTenantForm({
      tenantName:         row.tenantName         || '',
      monthlyRent:        row.monthlyRent        != null ? String(row.monthlyRent)        : '',
      leaseStartDate:     row.leaseStartDate     ? row.leaseStartDate.slice(0, 10)        : '',
      leaseEndDate:       row.leaseEndDate       ? row.leaseEndDate.slice(0, 10)          : '',
      status:             row.status             || 'Active',
      paymentDueDay:      row.paymentDueDay      != null ? String(row.paymentDueDay)      : '',
      reminderDaysBefore: row.reminderDaysBefore != null ? String(row.reminderDaysBefore) : '30',
    });
    setTenantModal(row);
  };
  const saveTenant = async () => {
    if (!tenantForm.tenantName?.trim()) return;
    setTenantSaving(true);
    try {
      const isAdd = tenantModal === 'add';
      const payload = {
        tenantName:         tenantForm.tenantName.trim(),
        monthlyRent:        tenantForm.monthlyRent        !== '' ? parseFloat(tenantForm.monthlyRent)        : null,
        leaseStartDate:     tenantForm.leaseStartDate     || null,
        leaseEndDate:       tenantForm.leaseEndDate       || null,
        status:             tenantForm.status             || 'Active',
        paymentDueDay:      tenantForm.paymentDueDay      !== '' ? parseInt(tenantForm.paymentDueDay)        : null,
        reminderDaysBefore: tenantForm.reminderDaysBefore !== '' ? parseInt(tenantForm.reminderDaysBefore)   : 30,
      };
      const base = `deals/${deal.dealID}/details/${detail.dealDetailID}/tenants`;
      const saved = isAdd
        ? await api.post(base, payload)
        : await api.put(`${base}/${tenantModal.rentID}`, payload);
      setTenants(prev => isAdd ? [...prev, saved] : prev.map(t => t.rentID === tenantModal.rentID ? saved : t));
      setTenantModal(null);
    } catch (e) { toast.error(e.message); }
    finally { setTenantSaving(false); }
  };
  const deleteTenant = async (row) => {
    if (!window.confirm(`Remove tenant "${row.tenantName}"?`)) return;
    try {
      await api.del(`deals/${deal.dealID}/details/${detail.dealDetailID}/tenants/${row.rentID}`);
      setTenants(prev => prev.filter(t => t.rentID !== row.rentID));
    } catch (e) { toast.error(e.message); }
  };

  // Segment (payment term) CRUD
  //
  // THE AMORTIZATION PERIOD IS EDITED HERE TOO, BUT IT IS STILL THE NOTE'S.
  // It sizes a P&I payment, so the person choosing "PMT" on a term is the
  // person who needs to set it — and the first cut of this modal only pointed
  // them at the capital-item form, so a term was saved as PMT with no period
  // and amortized the whole balance to its end date, silently and by design.
  // The field is seeded from `detail.amortizationMonths` (never from the
  // segment row — LoanScheduleSegments carries no such column, see migration
  // 185) and, when changed, is written to the CAPITAL ITEM on save, so every
  // P&I term on the note reads the same term. It is one fact about the loan
  // wherever it is typed.
  const noteAmortizationInput = () => (detail.amortizationMonths != null ? String(detail.amortizationMonths) : '');
  const openAddSegment = () => {
    setSegForm({ effectiveDate: '', endDate: '', paymentDayConvention: 'LoanDate', paymentDayOfMonth: '', paymentType: 'Monthly', rateType: 'Fixed', fixedRate: '', rateIndexId: '', spread: '', floor: '', ceiling: '', fixedPaymentAmount: '', principlePaymentType: 'InterestOnly', dayCountConvention: '30/360', principalReductionAmount: '', principalReductionPercent: '', amortizationMonths: noteAmortizationInput() });
    setSegModal('add');
  };
  const openEditSegment = (row) => {
    setSegForm({
      effectiveDate:        row.effectiveDate        ? row.effectiveDate.slice(0, 10)      : '',
      endDate:              row.endDate              ? row.endDate.slice(0, 10)             : '',
      paymentDayConvention: row.paymentDayConvention || 'LoanDate',
      paymentDayOfMonth:    row.paymentDayOfMonth    != null ? String(row.paymentDayOfMonth) : '',
      paymentType:          row.paymentType          || 'Monthly',
      rateType:             row.rateType             || 'Fixed',
      fixedRate:            row.fixedRate            != null ? String(Number((row.fixedRate  * 100).toPrecision(10))) : '',
      rateIndexId:          row.rateIndexID          || '',
      spread:               row.spread               != null ? String(Number((row.spread    * 100).toPrecision(10))) : '',
      floor:                row.floor                != null ? String(Number((row.floor     * 100).toPrecision(10))) : '',
      ceiling:              row.ceiling              != null ? String(Number((row.ceiling   * 100).toPrecision(10))) : '',
      fixedPaymentAmount:   row.fixedPaymentAmount   != null ? String(row.fixedPaymentAmount) : '',
      principlePaymentType:       row.principlePaymentType       || 'InterestOnly',
      dayCountConvention:          (() => { const v = row.dayCountConvention; if (!v && v !== 0) return '30/360'; const s = String(v); return s === '360' ? '30/360' : s === '365' ? 'Actual/Actual' : s; })(),
      principalReductionAmount:    row.principalReductionAmount    != null ? String(row.principalReductionAmount)  : '',
      principalReductionPercent:   row.principalReductionPercent   != null ? String(Number((row.principalReductionPercent * 100).toPrecision(10))) : '',
      amortizationMonths:          noteAmortizationInput(),
    });
    setSegModal(row);
  };
  const saveSegment = async () => {
    if (!segForm.rateType || !segForm.paymentType) return;
    setSegSaving(true);
    try {
      const isAdd = segModal === 'add';
      const payload = {
        effectiveDate:        segForm.effectiveDate     || null,
        endDate:              segForm.endDate           || null,
        paymentDayConvention: segForm.paymentDayConvention || 'LoanDate',
        paymentDayOfMonth:    segForm.paymentDayConvention === 'Other' && segForm.paymentDayOfMonth !== ''
                                ? parseInt(segForm.paymentDayOfMonth) : null,
        paymentType:          segForm.paymentType,
        rateType:             segForm.rateType,
        floor:                segForm.floor   !== '' ? parseFloat(segForm.floor)   / 100 : null,
        ceiling:              segForm.ceiling !== '' ? parseFloat(segForm.ceiling) / 100 : null,
        fixedPaymentAmount:   segForm.fixedPaymentAmount !== '' ? parseFloat(segForm.fixedPaymentAmount) : null,
        principlePaymentType:       segForm.principlePaymentType || 'InterestOnly',
        dayCountConvention:          segForm.dayCountConvention || '30/360',
        principalReductionAmount:    segForm.principlePaymentType === 'ScheduledReduction' && segForm.principalReductionAmount  !== '' ? parseFloat(segForm.principalReductionAmount)         : null,
        principalReductionPercent:   segForm.principlePaymentType === 'ScheduledReduction' && segForm.principalReductionPercent !== '' ? parseFloat(segForm.principalReductionPercent) / 100 : null,
        ...(segForm.rateType === 'Fixed' ? {
          fixedRate: segForm.fixedRate !== '' ? parseFloat(segForm.fixedRate) / 100 : null,
        } : {
          rateIndexId: segForm.rateIndexId || null,
          spread:      segForm.spread !== '' ? parseFloat(segForm.spread) / 100 : null,
        }),
      };
      const base = `deals/${deal.dealID}/details/${detail.dealDetailID}/segments`;
      const saved = isAdd
        ? await api.post(base, payload)
        : await api.put(`${base}/${segModal.segmentID}`, payload);
      setSegments(prev => isAdd ? [...prev, saved] : prev.map(s => s.segmentID === segModal.segmentID ? saved : s));
      // The amortization period goes to the NOTE, not the segment (see above).
      // Only when it actually changed: a no-op PUT would still kick the Sage
      // entity reconcile the detail update runs. Blank is sent as null, which
      // the API reads as an explicit CLEAR — every other field on that route
      // is COALESCE'd and cannot be emptied, and this one has to be.
      // Gated on PMT as well: the field is only shown for a P&I term, so a value
      // typed and then hidden by switching the type back must not land.
      if (payload.principlePaymentType === 'PMT' && segForm.amortizationMonths !== noteAmortizationInput()) {
        await api.put(`deals/${deal.dealID}/details/${detail.dealDetailID}`, {
          amortizationMonths: segForm.amortizationMonths !== '' ? parseInt(segForm.amortizationMonths, 10) : null,
        });
        if (onUpdated) onUpdated();
      }
      setSegModal(null);
    } catch (e) { toast.error(e.message); }
    finally { setSegSaving(false); }
  };
  const deleteSegment = async (row) => {
    if (!window.confirm('Remove this payment term?')) return;
    try {
      await api.del(`deals/${deal.dealID}/details/${detail.dealDetailID}/segments/${row.segmentID}`);
      setSegments(prev => prev.filter(s => s.segmentID !== row.segmentID));
    } catch (e) { toast.error(e.message); }
  };

  // Prospect CRUD
  const openNewProspect = () => {
    setEditingProspect(null);
    setProspectForm(EMPTY_PROSPECT_FORM);
    setShowProspectForm(true);
  };
  const openEditProspect = (row) => {
    setEditingProspect(row.prospectID);
    setProspectForm({
      customer:     row.customer     || '',
      salesPrice:   row.salesPrice   != null ? String(row.salesPrice)   : '',
      acres:        row.acres        != null ? String(row.acres)        : '',
      pricePerAcre: row.pricePerAcre != null ? String(row.pricePerAcre) : '',
      status:       row.status       || 'Likely',
      closingDate:  row.closingDate  ? row.closingDate.slice(0, 10)     : '',
      notes:        row.notes        || '',
      parcelIds:    (row.parcelIDs || '').split(',').filter(Boolean),
    });
    setShowProspectForm(true);
  };
  const saveProspect = async () => {
    if (!prospectForm.customer?.trim()) return;
    setProspectSaving(true);
    const payload = {
      customer:     prospectForm.customer.trim(),
      salesPrice:   prospectForm.salesPrice   !== '' ? parseFloat(prospectForm.salesPrice)   : null,
      acres:        prospectForm.acres        !== '' ? parseFloat(prospectForm.acres)        : null,
      pricePerAcre: prospectForm.pricePerAcre !== '' ? parseFloat(prospectForm.pricePerAcre) : null,
      status:       prospectForm.status || 'Likely',
      closingDate:  prospectForm.closingDate  || null,
      notes:        prospectForm.notes        || null,
      parcelIds:    prospectForm.parcelIds    || [],
    };
    try {
      const base = `deals/${deal.dealID}/details/${detail.dealDetailID}/prospects`;
      if (editingProspect) {
        const saved = await api.put(`${base}/${editingProspect}`, payload);
        setProspects(prev => prev.map(p => (p.prospectID === editingProspect ? saved : p)));
      } else {
        const saved = await api.post(base, payload);
        setProspects(prev => [saved, ...prev]);
      }
      setProspectForm(EMPTY_PROSPECT_FORM);
      setEditingProspect(null);
      setShowProspectForm(false);
    } catch (e) { toast.error(e.message); }
    finally { setProspectSaving(false); }
  };
  const deleteProspect = async (row) => {
    if (!window.confirm('Remove this prospect entry?')) return;
    try {
      await api.del(`deals/${deal.dealID}/details/${detail.dealDetailID}/prospects/${row.prospectID}`);
      setProspects(prev => prev.filter(p => p.prospectID !== row.prospectID));
    } catch (e) { toast.error(e.message); }
  };

  const typeLabel     = DETAIL_TYPE_OPTIONS.find(o => o.value === detail.detailType)?.label || detail.detailType;
  const locationLabel = (sageLocations || []).find(l => l.sageLocationID === detail.sageLocationID)?.displayName || detail.sageLocationName || null;
  const accountMatch  = (sageAccounts  || []).find(a => a.sageAccountID  === detail.sageAccountID);
  const accountLabel  = accountMatch ? `${accountMatch.accountNumber}${accountMatch.accountName ? ' — ' + accountMatch.accountName : ''}` : null;
  const sageMissing   = !accountLabel && !locationLabel;
  // TWO COST FIGURES, AND THE HEADER SHOWS BOTH WHENEVER THEY DIFFER.
  //   sageTotalCost — the GROSS cost of the asset (debt-funded portion included)
  //   deployedCapital — what is left once the borrowing nets off: the equity in
  //   outstandingDebt — the difference, and the reason there are two
  // sageDebt arrives as a SIGNED GL balance, so a liability is negative and the
  // amount outstanding is its negation. NULL means "this detail has no debt
  // breakout" (an N/R loan, or a pre-migration schema) — deliberately not 0, so
  // an unanswerable question never renders as a confident $0 Debt row.
  const sageAmounts     = detail.sageAmounts || {};
  const deployedCapital = Number(sageAmounts.sageAmount) || 0;
  const sageTotalCost   = sageAmounts.sageTotalCost != null ? Number(sageAmounts.sageTotalCost) : deployedCapital;
  const outstandingDebt = sageAmounts.sageDebt != null ? -Number(sageAmounts.sageDebt) : null;
  // Half a cent, so a rounding tail in DECIMAL(19,4) cannot put a $0 Debt row on
  // an unlevered deal.
  const hasDebt         = outstandingDebt != null && Math.abs(outstandingDebt) >= 0.005;
  // Total Cost, then the debt, then what we actually put in — read top to
  // bottom it is the subtraction, which is why the order is not alphabetical.
  const costRows        = (label = 'Total Cost') => (hasDebt
    ? [[label, fmt.currency(sageTotalCost)],
       ['Debt', fmt.currency(outstandingDebt)],
       ['Deployed Capital', fmt.currency(deployedCapital)]]
    : [[label, fmt.currency(sageTotalCost)]]);

  const isInvest = detail.detailType === 'Active' || detail.detailType === 'Passive';
  const isNR     = detail.detailType === 'N/R';
  // The dates the projection actually uses, resolved by the API from the
  // payment terms (getDealDetailSvc -> loanTerms). NEVER detail.loanDate /
  // detail.maturityDate: those are the retired typed columns, which the
  // payment terms now outrank, and reading them here is how this header came
  // to disagree with the Cash Flow Report about when a note matures. The
  // fallback is for a payload from an API deploy that predates the field.
  const loanTerms = detail.loanTerms || {
    loanDate: detail.loanDate || null,
    maturityDate: detail.maturityDate || null,
    ignoredColumns: [],
  };
  const isRE     = detail.detailType === 'R/E';
  const today    = todayIsoInZone();
  // N/R payment helpers
  const paidPayments  = (detail.payments || []).filter(p => p.amountCollected != null);
  const lastNRPayment = paidPayments.length > 0
    ? paidPayments.reduce((a, b) => (a.collectedDate || '') > (b.collectedDate || '') ? a : b)
    : null;
  const nextNRPayment = (detail.payments || [])
    .filter(p => p.amountCollected == null)
    .sort((a, b) => (a.paymentDueDate || '').localeCompare(b.paymentDueDate || ''))[0] || null;
  // R/E helpers
  const totalAcres       = isRE ? (detail.parcels   || []).reduce((sum, p) => sum + (p.regridAcres || 0), 0) : 0;
  const primaryProspect  = isRE ? ((detail.prospects || [])[0] || null) : null;
  const lastSageTxnDate  = detail.sageAmounts?.lastTransactionDate || null;

  const rowActions = (editFn, delFn) => (
    <div style={{ display: 'flex', gap: 6 }}>
      <Btn size="sm" variant="ghost" style={{ padding: '3px 8px', fontSize: 11 }} onClick={editFn}>Edit</Btn>
      <Btn size="sm" variant="danger" style={{ padding: '3px 8px', fontSize: 11 }} onClick={delFn}>Remove</Btn>
    </div>
  );

  const detailButtons = (
    <Btn size="sm" variant="secondary" onClick={openEdit}>Edit</Btn>
  );

  const detailBody = (
    <>
      {sageMissing && (
        <div style={{ marginBottom: 12 }}>
          <Badge color={B.red}>Need Sage Info</Badge>
        </div>
      )}
      {!mapFocus && <InfoGrid items={[
        // Lead/Status: only show when detail-level value differs from deal
        ...(detail.harvusLeadNames && detail.harvusLeadNames !== deal.harvusLeadNames ? [['Lead', detail.harvusLeadNames]] : []),
        ...(!isInvest && detail.status && detail.status !== deal.status ? [['Status', <StatusBadge status={detail.status} />]] : []),

        // Active / Passive
        ...(isInvest ? [
          ...(detail.committedCapital != null ? [['Amount',      fmt.currency(detail.committedCapital)]] : []),
          // TOTAL COST IS ALWAYS SHOWN, on every capital type. It was
          // conditional on there being debt, which meant an unlevered
          // Active/Passive detail displayed no Total Cost anywhere at all —
          // the reader could not tell "no debt" from "not reported". On an
          // unlevered deal it equals Net Invested, and the two agreeing IS the
          // information. Only the Debt row stays conditional, since a $0 Debt
          // line on an unlevered deal is noise.
          ['Total Cost', fmt.currency(sageTotalCost)],
          ...(hasDebt ? [['Debt', fmt.currency(outstandingDebt)]] : []),
          // "Net Invested" already meant deployed capital, so it keeps its name.
          ['Net Invested', fmt.currency(deployedCapital)],
          ...(detail.estimatedIRR != null    ? [['Est. IRR',     fmt.pct(detail.estimatedIRR)]]         : []),
          ...(detail.actualIRR    != null    ? [['Actual IRR',   fmt.pct(detail.actualIRR)]]            : []),
        ] : []),

        // N/R
        ...(isNR ? [
          // ORIGINATION FACE AMOUNT, and only where a legacy note still
          // carries one. Nothing types it any more — the live figure is the
          // Current Balance beside it, straight off the note's Sage account.
          ...(detail.loanAmount != null ? [['Loan Amount (orig.)', fmt.currency(detail.loanAmount)]] : []),
          ['Current Balance', fmt.currency(deployedCapital)],
          // THE RESOLVED DATES, not DealDetails.LoanDate/.MaturityDate. Those
          // are the typed columns, which the payment terms now outrank — the
          // header printing one while the Cash Flow Report schedules off the
          // other is how a mistyped maturity went unnoticed. The API resolves
          // it (getDealDetailSvc -> loanTerms) so the precedence has one home.
          ...((loanTerms.loanDate || loanTerms.maturityDate)
            ? [['Loan Date', `${fmt.date(loanTerms.loanDate)} → ${fmt.date(loanTerms.maturityDate)}`]] : []),
          ...(lastNRPayment ? [['Last Payment', fmt.date(lastNRPayment.collectedDate)]] : []),
          ...(nextNRPayment ? [['Next Payment', (
            <span style={{ color: (nextNRPayment.paymentDueDate || '').slice(0, 10) < today ? B.red : 'inherit' }}>
              {fmt.date(nextNRPayment.paymentDueDate)}
            </span>
          )]] : []),
        ] : []),

        // R/E
        ...(isRE ? [
          ...(detail.propertyType ? [['Property Type', detail.propertyType]] : []),
          ...(detail.holdType     ? [['Hold Type',     detail.holdType]]     : []),
          ...(totalAcres > 0      ? [['Total Acres',   totalAcres.toLocaleString('en-US', { maximumFractionDigits: 2 })]] : []),
          // AFS-specific
          ...(detail.holdType === 'Available for Sale (AFS)' ? [
            ...(detail.estimatedExit ? [['Est. Exit',   fmt.date(detail.estimatedExit)]] : []),
            ...costRows(),
            ...(primaryProspect ? [
              ...(primaryProspect.customer    ? [['Buyer',       primaryProspect.customer]]                                              : []),
              ...(primaryProspect.salesPrice != null ? [['Sale Price',  fmt.currency(primaryProspect.salesPrice)]]                       : []),
              ...(primaryProspect.closingDate ? [['Closing',     fmt.date(primaryProspect.closingDate)]]                                 : []),
              // Gross profit is against the GROSS cost — a sale price is what
              // the asset fetches, not what the equity clears, and the debt is
              // repaid out of it either way.
              ...(primaryProspect.salesPrice != null ? [['Est. GP', fmt.currency(primaryProspect.salesPrice - sageTotalCost)]]           : []),
            ] : []),
          ] : []),
          // L-T-specific
          ...(detail.holdType === 'Investment (L-T)' ? [
            ...(lastSageTxnDate             ? [['Last Payment', fmt.date(lastSageTxnDate)]]          : []),
            ...(detail.estimatedIRR != null ? [['Est. IRR',     fmt.pct(detail.estimatedIRR)]]       : []),
            ...costRows(),
          ] : []),
          // Generic (no hold type)
          ...(detail.holdType !== 'Available for Sale (AFS)' && detail.holdType !== 'Investment (L-T)' ? [
            ...(detail.budgetedCost       != null ? [['Budgeted Cost',        fmt.currency(detail.budgetedCost)]]       : []),
            ...(detail.budgetedSalesPrice != null ? [['Budgeted Sales Price', fmt.currency(detail.budgetedSalesPrice)]] : []),
            ...(detail.estimatedExit      ? [['Exit Date',       fmt.date(detail.estimatedExit)]]                       : []),
            ...costRows(),
          ] : []),
        ] : []),

        // Description: Active/Passive shown in header; others show here
        ...(!isInvest && detail.description ? [['Description', detail.description]] : []),
      ]} />}
      <Tabs
        tabs={[
          'Notes & Tasks',
          'Projects',
          ...(detail.detailType === 'N/R' ? ['Payment Schedule'] : []),
          'Transactions',
          'Contacts', 'Documents', 'Research', 'Chat',
          ...(detail.detailType === 'R/E' ? ['Tenants', 'Parcels', 'Map', 'Prospects'] : []),
          'Accounting',
        ]}
        active={activeTab}
        onChange={(t) => { setActiveTab(t); setMapFocus(false); }}
      />

      {activeTab === 'Payment Schedule' && (
        <div style={{ marginTop: 8 }}>
          {/* Payment Terms section */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: B.textMd }}>Payment Terms</span>
              <Btn size="sm" onClick={openAddSegment}>+ Add Term</Btn>
            </div>
            {/* A RETIRED COLUMN STILL CARRYING A VALUE THE TERMS OVERRODE.
                The three fields are gone from the edit modal, so this is the
                only place a legacy row can report one — and the row is where
                the reader can see the terms it disagrees with. Renders nothing
                on a clean note, which is almost all of them. */}
            {(loanTerms.ignoredColumns || []).map((ig) => (
              <div key={ig.field} style={{
                fontSize: 12, color: B.text, background: B.surfaceMuted,
                border: `1px solid ${B.amber}`, borderRadius: 6,
                padding: '6px 10px', marginBottom: 8,
              }}>
                A {ig.field === 'loanDate' ? 'loan date' : 'maturity date'} of{' '}
                <strong>{fmt.date(ig.typed)}</strong> is still recorded on this capital item and is
                not used — the payment terms below set it to <strong>{fmt.date(ig.used)}</strong>.
                Nothing reads the old value; it can only be cleared in the database.
              </div>
            ))}
            {segments.length === 0 ? (
              <div style={{ color: B.textLt, fontSize: 13, padding: '8px 0' }}>No payment terms defined.</div>
            ) : (
              <Table
                columns={[
                  { key: 'effectiveDate', label: 'Effective',   render: v => fmt.date(v) },
                  { key: 'endDate',       label: 'End Date',    render: v => v ? fmt.date(v) : '—' },
                  { key: 'rateType',      label: 'Type',        render: v => <StatusBadge status={v} /> },
                  { key: '_rate',         label: 'Rate',        render: (_, row) => row.rateType === 'Fixed'
                      ? fmt.pct(row.fixedRate)
                      : `${row.rateIndexName || '—'} + ${fmt.pct(row.spread)}` },
                  { key: 'paymentType',   label: 'Frequency' },
                  { key: '_pmtDate',      label: 'Pmt Date',    render: (_, row) => {
                      if (!row.paymentDayConvention || row.paymentDayConvention === 'LoanDate') return 'Loan Date';
                      if (row.paymentDayConvention === 'CalendarEnd') return 'Cal End';
                      if (row.paymentDayConvention === 'FirstDay')    return 'First Day';
                      if (row.paymentDayConvention === 'Other')       return `Day ${row.paymentDayOfMonth || '—'}`;
                      return row.paymentDayConvention;
                    }},
                  { key: 'principlePaymentType', label: 'Amortization', render: (v, row) => {
                    if (!v || v === 'InterestOnly') return 'Int. Only';
                    if (v === 'ScheduledReduction') return 'Sched.';
                    if (v === 'PMT') {
                      // A P&I payment is sized on the NOTE's amortization, so
                      // the term belongs beside the word — "PMT" alone reads
                      // identically whether the note amortizes over 20 years or
                      // repays inside this term, and those are payments an
                      // order of magnitude apart. It comes off the capital item
                      // rather than the row: one fact about the loan, shown
                      // wherever it applies.
                      const m = detail.amortizationMonths;
                      if (!m) return <span title="No amortization period set on this capital item — payments repay the whole balance over this term.">PMT <span style={{ color: B.amber }}>⚠</span></span>;
                      return `PMT · ${m % 12 === 0 ? `${m / 12}yr` : `${m}mo`}`;
                    }
                    return v;
                  }},
                  { key: 'dayCountConvention', label: 'Day Count', render: v => v || '—' },
                  { key: '_fixedAmt', label: 'Fixed Amt', render: (_, row) => row.fixedPaymentAmount != null ? fmt.currency(row.fixedPaymentAmount) : '—' },
                  { key: '_a', label: '', render: (_, row) => rowActions(() => openEditSegment(row), () => deleteSegment(row)) },
                ]}
                data={segments}
              />
            )}
          </div>

          {/* Scheduled Payments section */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: B.textMd }}>Scheduled Payments</span>
              <Btn size="sm" onClick={openGenModal}>Generate Payments</Btn>
            </div>
            {(detail.payments || []).length === 0 ? (
              <div style={{ color: B.textLt, fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
                No payments scheduled yet. Payments are auto-generated 20 days before due date based on the loan schedule.
              </div>
            ) : (
              <Table
                columns={[
                  { key: 'paymentDueDate',  label: 'Due Date',     render: v => fmt.date(v) },
                  { key: 'interestAmount',  label: 'Interest',     render: v => v != null ? fmt.currency(v) : '—' },
                  { key: 'principalAmount', label: 'Principal',    render: v => v != null ? fmt.currency(v) : '—' },
                  { key: 'scheduledAmount', label: 'Total',        render: v => fmt.currency(v) },
                  { key: 'amountCollected', label: 'Collected',    render: v => v != null ? fmt.currency(v) : '—' },
                  { key: 'collectedDate',   label: 'Collected On', render: v => fmt.date(v) },
                  { key: 'isGenerated',     label: 'Source',       render: v => <Badge color={v ? B.textLt : B.navy}>{v ? 'Auto' : 'Manual'}</Badge> },
                ]}
                data={detail.payments || []}
              />
            )}
          </div>
        </div>
      )}
      {activeTab === 'Transactions' && (() => {
        // TWO LEDGERS ON ONE TAB, KEPT APART. The capital item's own postings
        // (its Sage location or account) and, beneath them, the postings of
        // each Sage entity attached to the DEAL. An entity belongs to the deal
        // and to no capital item, so its money is inside the deal's Total Cost
        // and actual IRR and was on no ledger anywhere on this page — Beartail
        // printed an entity Total Cost on the strip above a tab that said "No
        // transactions synced yet". Never merged into the rows above: the
        // class subtotals there have to agree with THIS item's header, which
        // is summed from its own location alone.
        const own = detail.sageTransactions || [];
        const entityLists = (detail.sageEntityTransactions || []).filter(g => (g.rows || []).length);
        const untagged = !detail.sageLocationID && !detail.sageAccountID;
        if (own.length === 0 && entityLists.length === 0) {
          return (
            <div style={{ textAlign: 'center', padding: '32px 0', color: B.textLt, fontSize: 13, lineHeight: 1.5 }}>
              {/* "Run a Sage import" is the wrong advice for an item nobody has
                  tagged — no import can post to a location it was never given. */}
              {untagged
                ? <>This capital item isn't tagged to a Sage location or account, so it has no postings of its own. Edit it to add one.</>
                : <>No transactions synced yet. Run a Sage import to populate this data.</>}
            </div>
          );
        }
        return (
          <div style={{ marginTop: 8 }}>
            {/* The export is the WHOLE ledger, not the page above it — see
                exportTransactionsXlsx. Saying so on the button is what stops
                a longer file reading as a bug. It carries the entity postings
                too, one sheet each, so it is offered whichever list has rows. */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <TransactionsExportButton dealId={deal.dealID} dealDetailId={detail.dealDetailID} />
            </div>
            {own.length > 0 ? (
              <SageTransactionGroups
                rows={own}
                truncated={detail.sageTransactionsTruncated}
                limit={detail.sageTransactionLimit}
              />
            ) : (
              <div style={{ fontSize: 13, color: B.textMd, padding: '8px 4px 16px', lineHeight: 1.5 }}>
                This capital item has no postings of its own
                {untagged ? ' — it isn\'t tagged to a Sage location or account' : ''}.
                The postings below belong to the Sage entities attached to the deal.
              </div>
            )}
            {entityLists.map(g => (
              <div key={g.dealSageEntityID} style={{ marginTop: own.length ? 28 : 0 }}>
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                  padding: '8px 10px', marginBottom: 12, borderRadius: 8,
                  background: B.surfaceMuted, border: `1px solid ${B.border}`,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: B.textMd }}>
                    Sage entity
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{g.displayName || g.sageCode}</span>
                  <span style={{ fontSize: 11, color: B.textLt, fontFamily: 'monospace' }}>{g.sageCode}</span>
                  <span style={{ fontSize: 12, color: B.textLt }}>
                    Attached to the deal, not to this capital item — counted in the deal's Total Cost, not in this item's.
                  </span>
                </div>
                <SageTransactionGroups rows={g.rows} truncated={g.truncated} limit={detail.sageTransactionLimit} />
              </div>
            ))}
          </div>
        );
      })()}
      {activeTab === 'Notes & Tasks' && (
        <NotesTasksPanel dealId={deal.dealID} dealDetailId={detail.dealDetailID} users={users} includeDealLevel={includeDealLevelNotes} targetNoteId={targetNoteId} onNoteTargetHandled={onNoteTargetHandled} />
      )}
      {activeTab === 'Projects' && (
        <DealProjectsPanel dealId={deal.dealID} dealDetailId={detail.dealDetailID} users={users} includeDealLevel={includeDealLevelNotes} />
      )}
      {activeTab === 'Contacts' && (
        <DealContactsTab dealId={deal.dealID} dealDetailId={detail.dealDetailID} />
      )}
      {activeTab === 'Research' && (
        <DealResearchTab dealId={deal.dealID} />
      )}
      {activeTab === 'Chat' && (
        <ChatPanel dealId={deal.dealID} dealDetailId={detail.dealDetailID} />
      )}
      {activeTab === 'Documents' && (
        <DocumentsPanel
          dealId={deal.dealID}
          dealDetailId={detail.dealDetailID}
          detailType={detail.detailType}
          combinedFolders={unifiedMode}
          includeDealLevel={unifiedMode}
          docs={detailDocs}
          onRefresh={refreshDocs}
        />
      )}

      {activeTab === 'Tenants' && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <Btn size="sm" onClick={openAddTenant}>+ Add Tenant</Btn>
          </div>
          {tenants.length === 0 ? (
            <div style={{ color: B.textLt, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>No tenants on record.</div>
          ) : (
            <Table
              columns={[
                { key: 'tenantName',     label: 'Tenant' },
                { key: 'monthlyRent',    label: 'Monthly Rent', render: v => fmt.currency(v) },
                { key: 'leaseStartDate', label: 'Lease Start',  render: v => fmt.date(v) },
                { key: 'leaseEndDate',   label: 'Lease End',    render: v => fmt.date(v) },
                { key: 'status',         label: 'Status',       render: v => <StatusBadge status={v} /> },
                { key: '_a', label: '', render: (_, row) => rowActions(() => openEditTenant(row), () => deleteTenant(row)) },
              ]}
              data={tenants}
            />
          )}
        </div>
      )}

      {activeTab === 'Parcels' && (
        <ParcelsList dealDetailId={detail.dealDetailID} />
      )}

      {activeTab === 'Map' && (
        <ParcelsMap dealDetailId={detail.dealDetailID} onExpandChange={setMapFocus} />
      )}

      {activeTab === 'Accounting' && (() => {
        const isRE = detail.detailType === 'R/E';
        const subTabs = [
          'Sage Entities',
          ...(isRE ? ['Valuation', 'Budget', 'Sage JE'] : []),
          ...(isInvest ? ['Capital Schedule'] : []),
        ];
        const sub = subTabs.includes(accountingTab) ? accountingTab : 'Sage Entities';
        const se = deal.sageEntities;
        // The strip renders nothing when no entity is counted (and, for a
        // non-Admin, offers nothing to add) — on its own tab that would be a
        // blank page, so the empty state is said in words here.
        const noEntities = !se || !se.available || !(se.entities || []).length;
        return (
          <div style={{ marginTop: 8 }}>
            {subTabs.length > 1 && (
              <Tabs tabs={subTabs} active={sub} onChange={setAccountingTab} />
            )}
            {sub === 'Sage Entities' && (
              <div style={{ marginTop: 8 }}>
                {/* Deal-level: the entity belongs to the deal, not to this
                    capital item, so every capital item shows the same list. */}
                {noEntities && (
                  <div style={{ fontSize: 13, color: B.textMd, padding: '8px 4px 12px', lineHeight: 1.5 }}>
                    {se && se.available === false
                      ? 'Sage entities are not available on this database yet.'
                      : 'No Sage entities are counted in this deal.'}
                  </div>
                )}
                <DealSageEntitiesStrip
                  sageEntities={se}
                  dealId={deal.dealID}
                  dealName={deal.name}
                  canEdit={user?.role === 'Admin'}
                  onChanged={onDealChanged}
                />
              </div>
            )}
            {sub === 'Valuation' && (
              // The SIMPLE alternative to Budget: three ways to say what this is
              // worth, how long it would take to sell, and the IRR that implies.
              <ValuationTab dealId={deal.dealID} dealDetailId={detail.dealDetailID} />
            )}
            {sub === 'Budget' && (
              <RealEstateBudgetTab dealId={deal.dealID} dealDetailId={detail.dealDetailID} />
            )}
            {sub === 'Sage JE' && (
              <SageJournalTab dealId={deal.dealID} dealDetailId={detail.dealDetailID} />
            )}
            {sub === 'Capital Schedule' && (
              <CapitalScheduleTab dealId={deal.dealID} dealDetailId={detail.dealDetailID} />
            )}
          </div>
        );
      })()}


      {activeTab === 'Prospects' && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <Btn size="sm" onClick={openNewProspect}>+ Add Prospect</Btn>
          </div>
          {prospects.length === 0 && (
            <div style={{ color: B.textLt, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>No prospects on record.</div>
          )}
          {prospects.map(p => (
            <div key={p.prospectID} style={{ borderBottom: `1px solid ${B.border}`, padding: '12px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 14, marginRight: 10 }}>{p.customer}</span>
                  <Badge color={PROSPECT_STATUS_COLOR[p.status] || B.textMd}>{p.status || 'Likely'}</Badge>
                  {p.closingDate && <span style={{ marginLeft: 12, color: B.textMd, fontSize: 13 }}>Closing {fmt.date(p.closingDate)}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ color: B.textLt, fontSize: 12 }}>{fmt.date(p.createdUTC)}</span>
                  <Btn size="sm" variant="secondary" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => openEditProspect(p)}>Edit</Btn>
                  <Btn size="sm" variant="danger" style={{ padding: '3px 8px', fontSize: 11 }} onClick={() => deleteProspect(p)}>Remove</Btn>
                </div>
              </div>
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 18, fontSize: 13, color: B.textMd }}>
                {p.acres        != null && <span><span style={{ color: B.textLt }}>Acres:</span> {parseFloat(p.acres).toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>}
                {p.pricePerAcre != null && <span><span style={{ color: B.textLt }}>Price/acre:</span> {fmt.currency(p.pricePerAcre)}</span>}
                {p.salesPrice   != null && <span><span style={{ color: B.textLt }}>Sales price:</span> {fmt.currency(p.salesPrice)}</span>}
              </div>
              {p.parcelLabels && (
                <div style={{ marginTop: 4, fontSize: 12, color: B.textMd }}>
                  <span style={{ color: B.textLt }}>Parcels:</span> {p.parcelLabels}
                </div>
              )}
              {p.notes && <div style={{ marginTop: 6, color: B.textMd, fontSize: 13 }}>{p.notes}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Tenant add/edit modal */}
      {tenantModal && (
        <Modal title={tenantModal === 'add' ? 'Add Tenant' : 'Edit Tenant'} onClose={() => setTenantModal(null)} width={480}>
          <Input label="Tenant Name" value={tenantForm.tenantName} onChange={v => setTF('tenantName', v)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Monthly Rent ($)" type="number" value={tenantForm.monthlyRent} onChange={v => setTF('monthlyRent', v)} />
            <Select label="Status" value={tenantForm.status} onChange={v => setTF('status', v)} options={TENANT_STATUS_OPTIONS} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Lease Start" type="date" value={tenantForm.leaseStartDate} onChange={v => setTF('leaseStartDate', v)} />
            <Input label="Lease End"   type="date" value={tenantForm.leaseEndDate}   onChange={v => setTF('leaseEndDate',   v)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Payment Due Day"      type="number" value={tenantForm.paymentDueDay}      onChange={v => setTF('paymentDueDay',      v)} />
            <Input label="Reminder Days Before" type="number" value={tenantForm.reminderDaysBefore} onChange={v => setTF('reminderDaysBefore', v)} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setTenantModal(null)}>Cancel</Btn>
            <Btn disabled={tenantSaving || !tenantForm.tenantName?.trim()} onClick={saveTenant}>{tenantSaving ? 'Saving…' : 'Save'}</Btn>
          </div>
        </Modal>
      )}

      {/* Segment (payment term) add/edit modal */}
      {segModal && (
        <Modal title={segModal === 'add' ? 'Add Payment Term' : 'Edit Payment Term'} onClose={() => setSegModal(null)} width={560}>

          {/* ── Term Dates ── */}
          <SegSectionDivider label="Term Dates" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Effective Date" required type="date" value={segForm.effectiveDate} onChange={v => setSF('effectiveDate', v)} />
            <Input label="End Date" type="date" value={segForm.endDate} onChange={v => setSF('endDate', v)} />
          </div>
          <SegHelperText>End date is optional — leave blank if this rate term has no expiry.</SegHelperText>

          {/* ── Payment Schedule ── */}
          <SegSectionDivider label="Payment Schedule" />
          <Select label="Payment Frequency" value={segForm.paymentType} onChange={v => setSF('paymentType', v)} options={PAYMENT_TYPE_OPTIONS} />
          {segForm.paymentType !== 'Maturity' && (
            <>
              <Select label="Payment Date" value={segForm.paymentDayConvention} onChange={v => setSF('paymentDayConvention', v)} options={CONVENTION_OPTIONS} />
              {segForm.paymentDayConvention === 'Other' && (
                <>
                  <Input label="Day of Month" type="number" value={segForm.paymentDayOfMonth} onChange={v => setSF('paymentDayOfMonth', v)} placeholder="e.g. 15" />
                  <SegHelperText>Enter 1–28 to avoid month-length issues.</SegHelperText>
                </>
              )}
            </>
          )}

          {/* ── Interest Rate ── */}
          <SegSectionDivider label="Interest Rate" />
          <Select label="Rate Type" value={segForm.rateType} onChange={v => setSF('rateType', v)} options={RATE_TYPE_OPTIONS} />
          {segForm.rateType === 'Fixed' && (
            <Input label="Fixed Rate (%)" required type="number" value={segForm.fixedRate} onChange={v => setSF('fixedRate', v)} placeholder="e.g. 8 for 8%" />
          )}
          {segForm.rateType === 'Variable' && (
            <>
              <Select
                label="Rate Index"
                value={segForm.rateIndexId}
                onChange={v => setSF('rateIndexId', v)}
                options={[
                  { value: '', label: '— Select index —' },
                  // PERCENT-QUOTED INDEXES ONLY. dbo.RateIndexes also carries
                  // index LEVELS now (the S&P 500), and this list prices variable-
                  // rate loans: paymentGeneration reads the selected index's
                  // CurrentRate as a rate. Unfiltered, a note receivable could be
                  // pegged to the S&P at 6,450% + spread.
                  ...(rateIndexes || []).filter(r => !isLevelUnit(r.unit)).map(r => ({ value: r.indexID, label: `${r.displayName || r.indexName}${r.currentRate != null ? ` (${fmt.pct(r.currentRate)})` : ''}` })),
                ]}
              />
              <Input label="Spread (%)" type="number" value={segForm.spread} onChange={v => setSF('spread', v)} placeholder="e.g. 2.5 for 2.5%" />
            </>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Floor (%)" type="number" value={segForm.floor} onChange={v => setSF('floor', v)} placeholder="Optional minimum rate" />
            <Input label="Ceiling (%)" type="number" value={segForm.ceiling} onChange={v => setSF('ceiling', v)} placeholder="Optional maximum rate" />
          </div>

          {/* ── Principal & Amortization ── */}
          <SegSectionDivider label="Principal & Amortization" />
          <Select label="Amortization Type" value={segForm.principlePaymentType} onChange={v => setSF('principlePaymentType', v)} options={AMORTIZATION_TYPE_OPTIONS} />
          <Select label="Day Count Basis" value={segForm.dayCountConvention} onChange={v => setSF('dayCountConvention', v)} options={DAY_COUNT_OPTIONS} />
          {segForm.principlePaymentType === 'PMT' && (
            <>
              <Input label="Amortization Period (months)" type="number" value={segForm.amortizationMonths}
                onChange={v => setSF('amortizationMonths', v)} placeholder="e.g. 240 for a 20-year amortization" />
              <SegHelperText>
                What the payment is sized over — <strong>not</strong> how long this term runs. A
                20-year amortization on a note maturing sooner leaves a balloon at maturity, which
                is how most notes are written. This is the note’s amortization, shared by every
                P&amp;I term on it and shown on the capital item too; it does not change when the
                rate does. <strong>Left blank, payments repay the whole balance by this term’s end
                date</strong> — usually a payment several times too large.
              </SegHelperText>
            </>
          )}
          {segForm.principlePaymentType === 'ScheduledReduction' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Input label="Reduction per Period ($)" type="number" value={segForm.principalReductionAmount} onChange={v => { setSF('principalReductionAmount', v); if (v !== '') setSF('principalReductionPercent', ''); }} placeholder="Fixed dollar amount" />
                <Input label="Reduction per Period (%)" type="number" value={segForm.principalReductionPercent} onChange={v => { setSF('principalReductionPercent', v); if (v !== '') setSF('principalReductionAmount', ''); }} placeholder="e.g. 1 for 1%" />
              </div>
              <SegMutualNote />
            </>
          )}
          <Input label="Override Payment Amount ($)" type="number" value={segForm.fixedPaymentAmount} onChange={v => setSF('fixedPaymentAmount', v)} />
          <SegHelperText>Optional. Leave blank to calculate automatically from rate. Enter only when the loan document specifies an exact payment amount.</SegHelperText>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" onClick={() => setSegModal(null)}>Cancel</Btn>
            <Btn
              disabled={segSaving || !segForm.rateType || !segForm.paymentType || !segForm.effectiveDate
                || (segForm.rateType === 'Fixed'                          && !segForm.fixedRate)
                || (segForm.rateType === 'Variable'                       && !segForm.rateIndexId)
                || (segForm.paymentType !== 'Maturity' && segForm.paymentDayConvention === 'Other' && !segForm.paymentDayOfMonth)}
              onClick={saveSegment}
            >
              {segSaving ? 'Saving…' : 'Save'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Generate Payments modal */}
      {genModal && (
        <Modal title="Generate Scheduled Payments" onClose={() => setGenModal(false)} width={400}>
          <div style={{ fontSize: 13, color: B.textMd, marginBottom: 12 }}>
            Generate payment records for a date range based on the loan's payment terms. Existing payments are skipped.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="From Date" type="date" value={genForm.fromDate} onChange={v => setGF('fromDate', v)} />
            <Input label="To Date"   type="date" value={genForm.toDate}   onChange={v => setGF('toDate',   v)} />
          </div>
          {genResult && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: B.green + '18', borderRadius: 6, fontSize: 13, color: B.text }}>
              Generated {genResult.generated} payment{genResult.generated !== 1 ? 's' : ''}, skipped {genResult.skipped} existing.
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setGenModal(false)}>Close</Btn>
            <Btn disabled={genSaving || !genForm.fromDate || !genForm.toDate} onClick={submitGenPayments}>
              {genSaving ? 'Generating…' : 'Generate'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Prospect add/edit modal */}
      {showProspectForm && (
        <Modal title={editingProspect ? 'Edit Prospect' : 'Add Prospect'} onClose={() => setShowProspectForm(false)} width={560}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <Input label="Customer" value={prospectForm.customer} onChange={v => setProF('customer', v)} />
            <Select label="Status" value={prospectForm.status} onChange={v => setProF('status', v)}
              options={PROSPECT_STATUS_OPTIONS.map(s => ({ value: s, label: s }))} />
          </div>
          <div style={{ fontSize: 12, color: B.textLt, margin: '4px 0 6px' }}>
            Enter any two of Acres, Sales Price, and Price/Acre — the third is calculated automatically.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Input label="Acres"          type="number" value={prospectForm.acres}        onChange={v => setProEcon('acres',        v)} />
            <Input label="Sales Price ($)" type="number" value={prospectForm.salesPrice}   onChange={v => setProEcon('salesPrice',   v)} />
            <Input label="Price / Acre ($)" type="number" value={prospectForm.pricePerAcre} onChange={v => setProEcon('pricePerAcre', v)} />
          </div>
          <Input label="Closing Date" type="date" value={prospectForm.closingDate} onChange={v => setProF('closingDate', v)} />
          <Textarea label="Notes" value={prospectForm.notes} onChange={v => setProF('notes', v)} rows={3} />
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: B.text, marginBottom: 6 }}>Parcels</div>
            <ProspectParcelPicker
              dealDetailId={detail.dealDetailID}
              value={prospectForm.parcelIds}
              onChange={ids => setProF('parcelIds', ids)}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setShowProspectForm(false)}>Cancel</Btn>
            <Btn disabled={prospectSaving || !prospectForm.customer?.trim()} onClick={saveProspect}>{prospectSaving ? 'Saving…' : 'Save'}</Btn>
          </div>
        </Modal>
      )}

      {/* Detail edit modal */}
      {showEdit && (
        <Modal title="Edit Detail" onClose={() => setShowEdit(false)} width={560}>
          <Textarea label="Description *" value={editForm.description} onChange={v => setE('description', v)} rows={3} placeholder="Brief description of this asset…" />
          <Select label="Type *" value={editForm.detailType} onChange={v => setE('detailType', v)}
            options={DETAIL_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))} />
          {editForm.detailType !== detail.detailType && (
            <div style={{ margin: '4px 0 12px', padding: '8px 10px', background: B.surfaceMuted, border: `1px solid ${B.amber}`, borderRadius: 6, fontSize: 12, color: B.textMd }}>
              Changing the type from <strong>{detail.detailType}</strong> to <strong>{editForm.detailType}</strong>. Type-specific data (loan terms, parcels, tenants, etc.) is kept but hidden while this is a {editForm.detailType} detail — switch back to restore it.
            </div>
          )}
          <Select label="Status" value={editForm.status} onChange={v => setE('status', v)}
            options={DETAIL_STATUS_OPTIONS.map(s => ({ value: s, label: s || '— No status —' }))} />
          <HarvusLeadMultiSelect users={users} value={editForm.harvusLeadUserIDs} onChange={v => setE('harvusLeadUserIDs', v)} />
          {editForm.detailType === 'N/R' && (
            <>
              <Select label="Sage Account" value={editForm.sageAccountId} onChange={v => setE('sageAccountId', v)}
                options={[{ value: '', label: '— None —' }, ...(sageAccounts || []).map(a => ({ value: a.sageAccountID, label: `${a.accountNumber}${a.accountName ? ' — ' + a.accountName : ''}` }))]} />
              {/* LOAN AMOUNT / LOAN DATE / MATURITY ARE NOT EDITED HERE ANY
                  MORE. They were hand-typed copies of facts the platform
                  already holds, and the copy is the one that goes stale:
                  Watermark carried a maturity of 11/30/0206 — a mistyped 2026
                  — while its payment terms below ran into 2027, and the note
                  projected on the Cash Flow Report as interest that simply
                  stops with the $3.0M principal never coming back. The
                  principal now comes from the note's Sage receivable account
                  (the Current Balance on the header) and the two dates from
                  the payment terms, which is the record somebody actually
                  maintains. See resolveLoanTerms in api/src/loanSchedule.js. */}
              <div style={{ fontSize: 12, color: B.textLt }}>
                The loan amount comes from this note’s Sage account, and the loan and maturity
                dates from its payment terms below — add or edit a term to change them.
              </div>
              <Input label="Amortization Period (months)" type="number" value={editForm.amortizationMonths}
                onChange={v => setE('amortizationMonths', v)} placeholder="e.g. 240 for a 20-year amortization" />
              <div style={{ fontSize: 12, color: B.textLt, marginTop: -6 }}>
                What principal-and-interest payments are sized over — not how long the note runs.
                A 20-year amortization on a note maturing sooner leaves a balloon at maturity,
                which is how most notes are written. Leave blank to repay the whole balance
                across the payment term itself.
              </div>
            </>
          )}
          {editForm.detailType !== 'N/R' && (
            <Select label="Sage Location" value={editForm.sageLocationId} onChange={v => setE('sageLocationId', v)}
              options={[{ value: '', label: '— None —' }, ...(sageLocations || []).map(l => ({ value: l.sageLocationID, label: l.displayName || l.sageCode }))]} />
          )}
          {editForm.detailType === 'R/E' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Select label="Property Type" value={editForm.propertyType} onChange={v => setE('propertyType', v)} options={PROPERTY_TYPE_OPTIONS} />
                <Select label="Hold Type"     value={editForm.holdType}     onChange={v => setE('holdType',     v)} options={HOLD_TYPE_OPTIONS} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <Input label="Budgeted Cost ($)"         type="number" value={editForm.budgetedCost}       onChange={v => setE('budgetedCost',       v)} />
                <Input label="Budgeted Sales Price ($)"  type="number" value={editForm.budgetedSalesPrice} onChange={v => setE('budgetedSalesPrice', v)} />
                <Input label="Exit Date"                 type="date"   value={editForm.estimatedExit}      onChange={v => setE('estimatedExit',      v)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <Input label="Purchase Date"             type="date"   value={editForm.purchaseDate}       onChange={v => setE('purchaseDate',       v)} />
              </div>
            </>
          )}
          {(editForm.detailType === 'Active' || editForm.detailType === 'Passive') && (
            <>
              <Input label="Committed Capital ($)" type="number" value={editForm.committedCapital} onChange={v => setE('committedCapital', v)} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Input label="Est. IRR (%)" type="number" value={editForm.estimatedIRR} onChange={v => setE('estimatedIRR', v)} placeholder="e.g. 15 for 15%" />
                <Input label="Actual IRR (%)" type="number" value={editForm.actualIRR} onChange={v => setE('actualIRR', v)} placeholder="e.g. 12 for 12%" />
              </div>
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
            {/* A deal must keep >=1 detail — disable delete on the sole detail
                (fall back to enabled when the sibling count is unknown; the API
                enforces the rule regardless). */}
            <Btn variant="danger"
                 disabled={deleting || (deal?.details?.length ?? 2) <= 1}
                 title={(deal?.details?.length ?? 2) <= 1
                   ? 'A deal must keep at least one capital detail. Change its type instead, or delete the whole deal.'
                   : undefined}
                 onClick={deleteDetail}>{deleting ? 'Deleting…' : 'Delete'}</Btn>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="ghost" onClick={() => setShowEdit(false)}>Cancel</Btn>
              <Btn disabled={editSaving || !editForm.description?.trim()} onClick={saveEdit}>{editSaving ? 'Saving…' : 'Save Changes'}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );

  if (inline) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge color={DETAIL_TYPE_COLOR[detail.detailType] || B.navy}>{typeLabel}</Badge>
            {isInvest && detail.status && detail.status !== deal.status && (
              <StatusBadge status={detail.status} />
            )}
            {isInvest && detail.description && (
              <span style={{ fontWeight: 700, fontSize: 14, color: B.text }}>{detail.description}</span>
            )}
          </div>
          {detailButtons}
        </div>
        {detailBody}
      </div>
    );
  }

  return (
    <Modal
      title={<span><Badge color={DETAIL_TYPE_COLOR[detail.detailType] || B.navy}>{typeLabel}</Badge> {deal.name}</span>}
      onClose={onClose}
      width={760}
      headerAction={detailButtons}>
      {detailBody}
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Main Deals page
// ---------------------------------------------------------------------------
export const Deals = ({ user, targetDealId, targetDetailId, targetNoteId, onTargetHandled }) => {
  const { toast } = useToast();
  const { data: deals, loading, error, reload } = useApi(() => api.get('deals'), []);
  const { data: users }         = useApi(() => api.get('users'), []);
  const { data: sageLocations } = useApi(() => api.get('sage/locations'), []);
  const { data: sageAccounts }  = useApi(() => api.get('sage/accounts'), []);
  const { data: rateIndexes }   = useApi(() => api.get('rate-indexes'), []);

  const [selected,        setSelected]        = useState(null);  // deal row from list
  const [dealDetail,      setDealDetail]       = useState(null);  // full deal object from API
  // Quick Note offers to link a note to the deal on screen.
  usePublishQuickNoteContext(selected ? { type: 'Deal', id: selected.dealID, label: selected.name } : null);
  const [singleDetail,    setSingleDetail]     = useState(null);  // auto-loaded when exactly 1 detail
  const [multiDetailIdx,  setMultiDetailIdx]   = useState(0);     // active index for 2+ details sidebar
  const [multiDetail,     setMultiDetail]      = useState(null);  // full detail for active sidebar item
  // A 2+ detail deal shows a synthetic "Underwriting" entry in the detail
  // sidebar as the home for deal-level (DealDetailID IS NULL) notes/tasks/docs.
  // This flag = that entry is the active sidebar selection (UI-only; no such
  // DealDetail row exists).
  const [underwritingSelected, setUnderwritingSelected] = useState(false);
  const [ddTab,            setDdTab]            = useState('Notes & Tasks');
  const [search,           setSearch]          = useState('');
  const [sortBy,           setSortBy]          = useState('default');

  const [showNewDeal,  setShowNewDeal]  = useState(false);
  const [showBulkResearch, setShowBulkResearch] = useState(false);
  const [showNaicsBulk,  setShowNaicsBulk]  = useState(false);
  const [newDeal,      setNewDeal]      = useState({ name: '', detailType: 'R/E', notes: '', harvusLeadUserIDs: [], estimatedAmount: '', estimatedIRR: '', estimatedClose: '', dealStructure: '', updateFrequency: '', naicsCode: '', naicsDescription: '' });
  const [saving,       setSaving]       = useState(false);

  const [showEdit,     setShowEdit]     = useState(false);
  const [editDeal,     setEditDeal]     = useState({});
  const [editSaving,   setEditSaving]   = useState(false);
  const [deleting,     setDeleting]     = useState(false);

  const [showNewDetail, setShowNewDetail] = useState(false);
  const [newDetail,     setNewDetail]     = useState({ detailType: 'N/R', status: '', sageLocationId: '', sageAccountId: '', harvusLeadUserIDs: [], amortizationMonths: '', propertyType: '', holdType: '', budgetedCost: '', budgetedSalesPrice: '', estimatedExit: '', committedCapital: '', description: '' });
  const [detailSaving,  setDetailSaving]  = useState(false);

  // Inline status / lead edit (list view only)
  const [inlineEdit,   setInlineEdit]   = useState(null);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [inlinePos,    setInlinePos]    = useState(null);
  const inlineRef = useRef(null);
  useEffect(() => {
    if (!inlineEdit) return;
    const h = (e) => { if (inlineRef.current && !inlineRef.current.contains(e.target)) setInlineEdit(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [inlineEdit]);
  useLayoutEffect(() => {
    if (!inlineEdit || !inlineRef.current) { setInlinePos(null); return; }
    const h = inlineRef.current.offsetHeight;
    const margin = 8;
    const spaceBelow = window.innerHeight - inlineEdit.anchorBottom - margin;
    const spaceAbove = inlineEdit.anchorTop - margin;
    let top;
    if (h <= spaceBelow)      top = inlineEdit.anchorBottom + 4;
    else if (h <= spaceAbove) top = inlineEdit.anchorTop - h - 4;
    else                      top = Math.max(margin, window.innerHeight - h - margin);
    setInlinePos({ top });
  }, [inlineEdit]);

  const [showDead, setShowDead] = useState(false);

  // When navigated to a specific deal detail (e.g. from global search), remember
  // which detail to focus until the deal's full detail list has loaded.
  const [pendingDetailId, setPendingDetailId] = useState(null);
  // A note id to scroll to / highlight once the deal's notes feed has loaded
  // (captured locally because App clears targetNoteId as soon as we navigate).
  const [pendingNoteId, setPendingNoteId] = useState(null);

  const set   = (k, v) => setNewDeal(p => ({ ...p, [k]: v }));
  const setE  = (k, v) => setEditDeal(p => ({ ...p, [k]: v }));
  const setND = (k, v) => setNewDetail(p => ({ ...p, [k]: v }));

  const openDeal = async (row) => {
    setSelected(row);
    setDealDetail(null);
    setSingleDetail(null);
    setMultiDetailIdx(0);
    setMultiDetail(null);
    setUnderwritingSelected(false);
    setDdTab('Notes & Tasks');
    // withActiveDetail=1 embeds the first detail's full payload, so the page
    // renders in one round-trip instead of chaining a second detail request.
    try { setDealDetail(await api.get(`deals/${row.dealID}?withActiveDetail=1`)); } catch (e) { console.error('[deals] getDeal failed:', e); }
  };

  // Auto-open a deal when navigated from another page (e.g. Activity Report,
  // global search). An optional targetDetailId focuses a specific detail once
  // the deal's detail list has loaded.
  useEffect(() => {
    if (!targetDealId || !deals) return;
    const row = deals.find(d => d.dealID === targetDealId);
    if (row) { setPendingDetailId(targetDetailId || null); setPendingNoteId(targetNoteId || null); openDeal(row); onTargetHandled?.(); }
  }, [targetDealId, deals]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once the full deal (with its details) is loaded, focus the requested detail.
  useEffect(() => {
    if (!pendingDetailId || !dealDetail) return;
    const details = dealDetail.details || [];
    const idx = details.findIndex(d => d.dealDetailID === pendingDetailId);
    if (idx >= 0) setMultiDetailIdx(idx);
    setPendingDetailId(null);
  }, [dealDetail, pendingDetailId]);

  // Auto-fetch the full detail when a deal has exactly one — avoids the extra click
  useEffect(() => {
    if (!dealDetail || !selected) { setSingleDetail(null); return; }
    const details = dealDetail.details || [];
    if (details.length !== 1) { setSingleDetail(null); return; }
    // getDeal embeds the first detail as `activeDetail` — use it directly and
    // skip the extra round-trip.
    const ad = dealDetail.activeDetail;
    if (ad && ad.dealDetailID === details[0].dealDetailID) { setSingleDetail(ad); return; }
    setSingleDetail(null);
    api.get(`deals/${selected.dealID}/details/${details[0].dealDetailID}`)
      .then(setSingleDetail)
      .catch(e => console.error('[deals] single detail fetch failed:', e));
  }, [dealDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch the active detail when a deal has 2+ details
  useEffect(() => {
    if (!dealDetail || !selected) { setMultiDetail(null); return; }
    const details = dealDetail.details || [];
    if (details.length < 2) { setMultiDetail(null); return; }
    const active = details[multiDetailIdx];
    if (!active) { setMultiDetail(null); return; }
    // Use the embedded activeDetail for the default (first) selection to skip the
    // extra round-trip; fetch on demand when the user switches to another detail.
    const ad = dealDetail.activeDetail;
    if (ad && ad.dealDetailID === active.dealDetailID) { setMultiDetail(ad); return; }
    setMultiDetail(null);
    api.get(`deals/${selected.dealID}/details/${active.dealDetailID}`)
      .then(setMultiDetail)
      .catch(e => console.error('[deals] multi detail fetch failed:', e));
  }, [dealDetail, multiDetailIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshDeal = async () => {
    reload();
    if (!selected) return;
    try {
      const fresh = await api.get(`deals/${selected.dealID}?withActiveDetail=1`);
      setDealDetail(fresh);
      // Keep selected in sync with latest status/lead info
      setSelected(s => s ? { ...s, status: fresh.status, harvusLeadNames: fresh.harvusLeadNames, harvusLeadUserIDs: fresh.harvusLeadUserIDs } : s);
    } catch (e) { console.error('[deals] refreshDeal failed:', e); }
  };

  const openEdit = (deal) => {
    const leadIds = Array.isArray(deal.harvusLeadUserIDs)
      ? deal.harvusLeadUserIDs
      : (deal.harvusLeadUserIDs || '').split(',').filter(Boolean);
    setEditDeal({
      name:                deal.name                || '',
      status:              deal.status              || '',
      estimatedAmount:     deal.estimatedAmount     != null ? String(deal.estimatedAmount)     : '',
      estimatedIRR:        deal.estimatedIRR        != null ? String(Number((deal.estimatedIRR * 100).toPrecision(10))) : '',
      estimatedHoldMonths: deal.estimatedHoldMonths != null ? String(deal.estimatedHoldMonths) : '',
      dealStructure:       deal.dealStructure       || '',
      naicsCode:           deal.naicsCode           || '',
      naicsDescription:    deal.naicsDescription    || '',
      updateFrequency:     deal.updateFrequency     || '',
      harvusLeadUserIDs:   leadIds,
      notes:               deal.notes               || '',
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (!editDeal.name?.trim()) return;
    setEditSaving(true);
    try {
      await api.put(`deals/${selected.dealID}`, {
        name:                editDeal.name.trim(),
        status:              editDeal.status             || null,
        estimatedAmount:     editDeal.estimatedAmount    !== '' ? parseFloat(editDeal.estimatedAmount)    : null,
        estimatedIRR:        editDeal.estimatedIRR       !== '' ? parseFloat(editDeal.estimatedIRR) / 100 : null,
        estimatedHoldMonths: editDeal.estimatedHoldMonths !== '' ? parseInt(editDeal.estimatedHoldMonths) : null,
        dealStructure:       editDeal.dealStructure      || null,
        naicsCode:           editDeal.naicsCode,
        naicsDescription:    editDeal.naicsDescription,
        updateFrequency:     editDeal.updateFrequency    || null,
        harvusLeadUserIDs:   editDeal.harvusLeadUserIDs  || [],
        notes:               editDeal.notes              || null,
      });
      setShowEdit(false);
      await refreshDeal();
    } catch (e) { toast.error(e.message); }
    finally { setEditSaving(false); }
  };

  const deleteDeal = async () => {
    // A deal always carries >=1 detail, so deletion cascades to its details
    // (handled server-side). Warn about the cascade when there's more than the
    // implicit single detail.
    const detailCount = (dealDetail?.details || []).length;
    const confirmMsg = detailCount > 1
      ? `Delete deal "${selected.name}" and its ${detailCount} capital details? This cannot be undone.`
      : `Delete deal "${selected.name}"? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setDeleting(true);
    try {
      await api.del(`deals/${selected.dealID}`);
      setSelected(null); setDealDetail(null);
      reload();
    } catch (e) { toast.error(e.message); }
    finally { setDeleting(false); }
  };

  const createDeal = async () => {
    if (!newDeal.name.trim()) return;
    setSaving(true);
    const body = {
      name:               newDeal.name.trim(),
      // The deal's first capital detail is created server-side from this type
      // (every deal has >=1 detail); description defaults to the deal name.
      detailType:         newDeal.detailType         || 'R/E',
      notes:              newDeal.notes              || null,
      harvusLeadUserIDs:  newDeal.harvusLeadUserIDs  || [],
      estimatedAmount:    newDeal.estimatedAmount    !== '' ? parseFloat(newDeal.estimatedAmount)    : null,
      estimatedIRR:       newDeal.estimatedIRR       !== '' ? parseFloat(newDeal.estimatedIRR) / 100 : null,
      estimatedClose:     newDeal.estimatedClose     || null,
      dealStructure:      newDeal.dealStructure      || null,
      naicsCode:          newDeal.naicsCode          || '',
      naicsDescription:   newDeal.naicsDescription   || '',
      updateFrequency:    newDeal.updateFrequency    || null,
      // Stable idempotency key shared by the initial attempt and the confirm
      // resubmit, so a retry dedupes on the server instead of creating twice.
      clientRequestId:    crypto.randomUUID(),
    };
    try {
      let created;
      try {
        created = await api.post('deals', body);
      } catch (e) {
        // The API gates creation: 409 with a `needs` payload means a duplicate
        // name and/or no Harvus lead. Confirm and resubmit with the flags.
        if (e.status === 409 && e.body?.needs) {
          const needs = e.body.needs;
          const flags = {};
          const lines = [];
          if (needs.duplicate) {
            const ex = needs.duplicate.exact?.[0];
            if (ex) {
              lines.push(`A deal named "${ex.Name}" already exists${ex.Status ? ` (status ${ex.Status})` : ''}.`);
            } else {
              const sim = (needs.duplicate.similar || []).slice(0, 3).map(d => d.Name).join(', ');
              lines.push(`Similar deals already exist: ${sim}.`);
            }
            flags.confirmDuplicate = true;
          }
          if (needs.leadMissing) {
            lines.push('No Harvus lead is assigned.');
            flags.skipLead = true;
          }
          lines.push('Create it anyway?');
          if (!window.confirm(lines.join(' '))) return;   // finally resets saving
          created = await api.post('deals', { ...body, ...flags });
        } else {
          throw e;
        }
      }
      setShowNewDeal(false);
      setNewDeal({ name: '', detailType: 'R/E', notes: '', harvusLeadUserIDs: [], estimatedAmount: '', estimatedIRR: '', estimatedClose: '', dealStructure: '', updateFrequency: '', naicsCode: '', naicsDescription: '' });
      reload();
      if (created?.dealID) openDeal(created);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const createDetail = async () => {
    setDetailSaving(true);
    try {
      await api.post(`deals/${selected.dealID}/details`, {
        detailType:         newDetail.detailType,
        status:             newDetail.status            || null,
        sageLocationId:     newDetail.sageLocationId    || null,
        sageAccountId:      newDetail.sageAccountId     || null,
        harvusLeadUserIDs:  newDetail.harvusLeadUserIDs || [],
        amortizationMonths: newDetail.amortizationMonths !== '' ? parseInt(newDetail.amortizationMonths, 10) : null,
        propertyType:       newDetail.propertyType       || null,
        holdType:           newDetail.holdType           || null,
        budgetedCost:       newDetail.budgetedCost        !== '' ? parseFloat(newDetail.budgetedCost)       : null,
        budgetedSalesPrice: newDetail.budgetedSalesPrice  !== '' ? parseFloat(newDetail.budgetedSalesPrice) : null,
        estimatedExit:      newDetail.estimatedExit       || null,
        committedCapital:   newDetail.committedCapital    !== '' ? parseFloat(newDetail.committedCapital)   : null,
        description:        newDetail.description        || null,
      });
      setShowNewDetail(false);
      setNewDetail({ detailType: 'N/R', status: '', sageLocationId: '', sageAccountId: '', harvusLeadUserIDs: [], amortizationMonths: '', propertyType: '', holdType: '', budgetedCost: '', budgetedSalesPrice: '', estimatedExit: '', committedCapital: '', description: '' });
      await refreshDeal();
    } catch (e) { toast.error(e.message); }
    finally { setDetailSaving(false); }
  };

  // Inline status (list view)
  const handleStatusClick = (e, row) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setInlineEdit({ dealID: row.dealID, type: 'status', current: row.status || '', x: rect.left, anchorTop: rect.top, anchorBottom: rect.bottom });
  };
  const saveInlineStatus = async (dealID, status) => {
    setInlineSaving(true);
    try { await api.put(`deals/${dealID}`, { status }); setInlineEdit(null); reload(); }
    catch (e) { toast.error(e.message); }
    finally { setInlineSaving(false); }
  };

  // Inline leads (list view)
  const handleLeadClick = (e, row) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const leads = Array.isArray(row.harvusLeadUserIDs) ? row.harvusLeadUserIDs
                : (row.harvusLeadUserIDs || '').split(',').filter(Boolean);
    setInlineEdit({ dealID: row.dealID, type: 'lead', current: leads, x: rect.left, anchorTop: rect.top, anchorBottom: rect.bottom });
  };
  const saveInlineLeads = async (dealID, harvusLeadUserIDs) => {
    try { await api.put(`deals/${dealID}`, { harvusLeadUserIDs }); reload(); }
    catch (e) { toast.error(e.message); }
  };

  const renderLeadCell = (v) => {
    const names = (v || '').split(',').map(n => n.trim().split(' ')[0]).filter(Boolean);
    return names.length ? names.join(', ') : <span style={{ color: B.textLt }}>—</span>;
  };

  // Sort + filter only when the inputs change — not on every keystroke-unrelated
  // re-render (opening the inline lead/status overlay, hover, etc.).
  const displayDeals = useMemo(
    () => sortDeals(filterDeals(deals || [], { search, showDead }), sortBy),
    [deals, search, showDead, sortBy],
  );

  if (loading) return <Spinner />;
  if (error)   return <ErrorMsg msg={error} onRetry={reload} />;

  // -------------------------------------------------------------------------
  // Full-page deal view
  // -------------------------------------------------------------------------
  if (selected) {
    const detailCount = (dealDetail?.details || []).length;
    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <Btn variant="ghost" size="sm" onClick={() => { setSelected(null); setDealDetail(null); }}><Icon name="arrowLeft" size={14} /> Deals</Btn>
          <div
            title="Edit deal"
            style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.4, color: B.text, cursor: 'pointer', borderBottom: `2px solid transparent`, transition: 'border-color 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.borderBottomColor = B.accent}
            onMouseLeave={e => e.currentTarget.style.borderBottomColor = 'transparent'}
            onClick={() => openEdit(dealDetail || selected)}
          >{selected.name}</div>
          <StatusBadge status={selected.status} />
          {(selected.harvusLeadNames || '').split(',').map(n => n.trim()).filter(Boolean).map(name => (
            <Badge key={name} color={B.navy} style={{ fontSize: 12 }}>{name}</Badge>
          ))}
          <Btn variant="ghost" size="sm" style={{ marginLeft: 'auto' }} onClick={() => openEdit(dealDetail || selected)}><Icon name="edit" size={14} /> Edit</Btn>
        </div>

        {/* The meta line now always renders, because DealNaicsChip carries its
            own "not set yet" state and the action that fixes it — gating the row
            on the two economics fields would hide the industry on exactly the
            deals that have no code. The chip renders nothing at all pre-169. */}
        <div style={{ marginBottom: 16, color: B.textMd, fontSize: 13, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          {selected.estimatedAmount != null && selected.estimatedAmount !== '' && (
            <span><span style={{ fontWeight: 600 }}>Est. Amount:</span> {fmt.currency(selected.estimatedAmount)}</span>
          )}
          {selected.updateFrequency && (
            <span><span style={{ fontWeight: 600 }}>Update Frequency:</span> {selected.updateFrequency}</span>
          )}
          <DealNaicsChip
            dealId={selected.dealID}
            canEdit={user?.role === 'Admin'}
            onChanged={reload}
          />
        </div>

        {/* Capital section toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Btn size="sm" onClick={() => {
            if (detailCount === 0) setND('description', selected.name || '');
            setShowNewDetail(true);
          }}>+ New Capital</Btn>
        </div>

        {/* Capital — always visible */}
        <div style={{ marginTop: 8 }}>
            {!dealDetail && <Spinner />}

            {/* 0 details — effectively unreachable now that every deal carries
                >=1 detail (legacy deals backfilled by migration 91; new deals
                seed a first detail on create). Kept as a lightweight safety net
                that points at "+ New Capital". Deal-level notes/tasks/docs live
                in the single-detail unified view and the 2+ "Deal-Level Notes &
                Docs" section below. */}
            {dealDetail && (dealDetail.details || []).length === 0 && (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: B.textMd }}>
                <div style={{ fontSize: 14, marginBottom: 12 }}>This deal has no capital detail yet.</div>
                <Btn size="sm" onClick={() => { setND('description', selected.name || ''); setShowNewDetail(true); }}>
                  + New Capital
                </Btn>
              </div>
            )}

            {/* 1 detail — render inline, no click needed */}
            {dealDetail && (dealDetail.details || []).length === 1 && (
              !singleDetail
                ? <Spinner />
                : <DetailModal
                    inline
                    unifiedMode
                    includeDealLevelNotes
                    deal={dealDetail || selected}
                    detail={singleDetail}
                    users={users}
                    sageLocations={sageLocations}
                    sageAccounts={sageAccounts}
                    rateIndexes={rateIndexes}
                    user={user}
                    onDealChanged={refreshDeal}
                    targetNoteId={pendingNoteId}
                    onNoteTargetHandled={() => setPendingNoteId(null)}
                    onClose={() => {}}
                    onUpdated={async () => { setSingleDetail(null); await refreshDeal(); }}
                  />
            )}

            {/* 2+ details — mini sidebar + inline detail */}
            {(dealDetail?.details || []).length > 1 && (
              <div style={{ display: 'flex', gap: 0, minHeight: 300 }}>
                {/* Mini sidebar */}
                <div style={{
                  width: 160, flexShrink: 0,
                  borderRight: `1px solid ${B.border}`,
                  paddingRight: 12, marginRight: 16,
                  display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                  {(dealDetail.details || []).map((dd, i) => {
                    const active = !underwritingSelected && i === multiDetailIdx;
                    // Lead with the real (Sage-derived) cost; fall back to the
                    // budgeted figure — shown in italics — when there are no
                    // actuals yet. The budgeted value is type-specific; R/E uses
                    // the budget/pro forma "Total Development Cost" (budgetTotalCost).
                    const budgeted = dd.detailType === 'N/R'    ? dd.loanAmount
                                   : dd.detailType === 'R/E'    ? dd.budgetTotalCost
                                   : (dd.detailType === 'Active' || dd.detailType === 'Passive')
                                     ? dd.committedCapital
                                   : null;
                    const hasActual = dd.sageActualCost != null && Number(dd.sageActualCost) !== 0;
                    const keyVal    = hasActual ? fmt.currency(dd.sageActualCost) : fmt.currency(budgeted);
                    const keyItalic = !hasActual && budgeted != null;
                    return (
                      <div key={dd.dealDetailID}
                        onClick={() => { setUnderwritingSelected(false); setMultiDetailIdx(i); }}
                        style={{
                          padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                          background: active ? B.navy : B.surfaceMuted,
                          border: `1px solid ${active ? B.navy : B.border}`,
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (!active) e.currentTarget.style.background = B.hover; }}
                        onMouseLeave={e => { if (!active) e.currentTarget.style.background = B.surfaceMuted; }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: active ? '#fff' : B.text }}>
                          {dd.description || dd.detailType}
                        </div>
                        {keyVal && (
                          <div style={{ fontSize: 11, color: active ? 'rgba(255,255,255,0.75)' : B.textMd, marginTop: 2, fontStyle: keyItalic ? 'italic' : 'normal' }}>
                            {keyVal}
                          </div>
                        )}
                        {dd.status && (
                          <div style={{ fontSize: 10, marginTop: 4, color: active ? 'rgba(255,255,255,0.6)' : B.textLt }}>
                            {dd.status}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Synthetic "Underwriting" entry — the home for deal-level
                      notes/tasks/docs (DealDetailID IS NULL). UI-only: no such
                      DealDetail row exists, so it never appears among real assets,
                      economics, or Sage roll-ups. Replaces the old separate
                      "Deal-Level Notes & Docs" toggle. */}
                  <div
                    onClick={() => setUnderwritingSelected(true)}
                    style={{
                      padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                      marginTop: 6, paddingTop: 12, borderTop: `1px solid ${B.border}`,
                      background: underwritingSelected ? B.navy : B.surfaceMuted,
                      border: `1px solid ${underwritingSelected ? B.navy : B.border}`,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (!underwritingSelected) e.currentTarget.style.background = B.hover; }}
                    onMouseLeave={e => { if (!underwritingSelected) e.currentTarget.style.background = B.surfaceMuted; }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: underwritingSelected ? '#fff' : B.text }}>
                      Underwriting
                    </div>
                    <div style={{ fontSize: 10, marginTop: 4, color: underwritingSelected ? 'rgba(255,255,255,0.6)' : B.textLt }}>
                      Deal-level notes & tasks
                    </div>
                  </div>
                </div>
                {/* Inline content — a real detail, or the deal-level "Underwriting" panel */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {underwritingSelected ? (
                    <div>
                      <Tabs tabs={['Notes & Tasks', 'Projects', 'Contacts', 'Documents', 'Chat']} active={ddTab} onChange={setDdTab} />
                      {ddTab === 'Notes & Tasks' && (
                        <NotesTasksPanel dealId={selected.dealID} users={users} targetNoteId={pendingNoteId} onNoteTargetHandled={() => setPendingNoteId(null)} />
                      )}
                      {ddTab === 'Projects' && (
                        <DealProjectsPanel dealId={selected.dealID} users={users} />
                      )}
                      {ddTab === 'Contacts' && (
                        <DealContactsTab dealId={selected.dealID} />
                      )}
                      {ddTab === 'Documents' && (
                        <DocumentsPanel
                          dealId={selected.dealID}
                          docs={(dealDetail || {}).documents || []}
                          onRefresh={refreshDeal}
                        />
                      )}
                      {ddTab === 'Chat' && (
                        <ChatPanel dealId={selected.dealID} />
                      )}
                    </div>
                  ) : !multiDetail ? (
                    <Spinner />
                  ) : (
                    <DetailModal
                      inline
                      unifiedMode
                      deal={dealDetail || selected}
                      detail={multiDetail}
                      users={users}
                      sageLocations={sageLocations}
                      sageAccounts={sageAccounts}
                      rateIndexes={rateIndexes}
                      user={user}
                      onDealChanged={refreshDeal}
                      targetNoteId={pendingNoteId}
                      onNoteTargetHandled={() => setPendingNoteId(null)}
                      onClose={() => {}}
                      onUpdated={async () => { setMultiDetail(null); await refreshDeal(); }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

        {/* New capital detail modal */}
        {showNewDetail && (
          <Modal title={`New Capital Detail — ${selected.name}`} onClose={() => setShowNewDetail(false)} width={560}>
            <Textarea label="Description *" value={newDetail.description} onChange={v => setND('description', v)} rows={2} placeholder="Brief description of this asset…" />
            <Select label="Type *" value={newDetail.detailType} onChange={v => setND('detailType', v)}
              options={DETAIL_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))} />
            {newDetail.detailType === 'N/R' && (
              <>
                <Select label="Sage Account" value={newDetail.sageAccountId} onChange={v => setND('sageAccountId', v)}
                  options={[{ value: '', label: '— None —' }, ...(sageAccounts || []).map(a => ({ value: a.sageAccountID, label: `${a.accountNumber}${a.accountName ? ' — ' + a.accountName : ''}` }))]} />
                {/* See the note in the Edit modal: these three are resolved
                    from the Sage account and the payment terms, not typed. */}
                <div style={{ fontSize: 12, color: B.textLt }}>
                  Add the payment terms once the capital item exists — the loan and maturity
                  dates come from them, and the amount from the Sage account above.
                </div>
                <Input label="Amortization Period (months)" type="number" value={newDetail.amortizationMonths}
                  onChange={v => setND('amortizationMonths', v)} placeholder="e.g. 240 for a 20-year amortization" />
              </>
            )}
            {newDetail.detailType !== 'N/R' && (
              <Select label="Sage Location" value={newDetail.sageLocationId} onChange={v => setND('sageLocationId', v)}
                options={[{ value: '', label: '— None —' }, ...(sageLocations || []).map(l => ({ value: l.sageLocationID, label: l.displayName || l.sageCode }))]} />
            )}
            {newDetail.detailType === 'R/E' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Select label="Property Type" value={newDetail.propertyType} onChange={v => setND('propertyType', v)}
                    options={PROPERTY_TYPE_OPTIONS} />
                  <Select label="Hold Type" value={newDetail.holdType} onChange={v => setND('holdType', v)}
                    options={HOLD_TYPE_OPTIONS} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <Input label="Budgeted Cost ($)"        type="number" value={newDetail.budgetedCost}       onChange={v => setND('budgetedCost',       v)} />
                  <Input label="Budgeted Sales Price ($)" type="number" value={newDetail.budgetedSalesPrice} onChange={v => setND('budgetedSalesPrice', v)} />
                  <Input label="Exit Date"                type="date"   value={newDetail.estimatedExit}      onChange={v => setND('estimatedExit',      v)} />
                </div>
              </>
            )}
            {(newDetail.detailType === 'Active' || newDetail.detailType === 'Passive') && (
              <Input label="Committed Capital ($)" type="number" value={newDetail.committedCapital} onChange={v => setND('committedCapital', v)} />
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <Btn variant="ghost" onClick={() => setShowNewDetail(false)}>Cancel</Btn>
              <Btn disabled={detailSaving || !newDetail.description?.trim()} onClick={createDetail}>{detailSaving ? 'Creating…' : 'Create Detail'}</Btn>
            </div>
          </Modal>
        )}

        {/* Edit deal modal */}
        {showEdit && (
          <Modal title={`Edit: ${selected.name}`} onClose={() => setShowEdit(false)} width={560}>
            <Input label="Name *" value={editDeal.name} onChange={v => setE('name', v)} required />
            <Select label="Status" value={editDeal.status} onChange={v => setE('status', v)}
              options={DEAL_STATUS_OPTIONS.map(s => ({ value: s, label: s || '— No status —' }))} />
            <HarvusLeadMultiSelect users={users} value={editDeal.harvusLeadUserIDs} onChange={v => setE('harvusLeadUserIDs', v)} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <Input label="Est. Amount ($)"    type="number" value={editDeal.estimatedAmount}     onChange={v => setE('estimatedAmount', v)} />
              <Input label="Est. IRR (%)"       type="number" value={editDeal.estimatedIRR}        onChange={v => setE('estimatedIRR', v)} />
              <Input label="Hold Period (mos.)" type="number" value={editDeal.estimatedHoldMonths} onChange={v => setE('estimatedHoldMonths', v)} />
            </div>
            <Input label="Deal Structure" value={editDeal.dealStructure} onChange={v => setE('dealStructure', v)} />
            <NaicsField
              value={editDeal.naicsCode}
              description={editDeal.naicsDescription}
              onChange={(code, title) => setEditDeal(p => ({ ...p, naicsCode: code, naicsDescription: title }))}
              hint="Optional — leave blank and it is derived from this deal’s documents. Choosing one keeps it from being changed automatically." />
            <Select label="Update Frequency" value={editDeal.updateFrequency} onChange={v => setE('updateFrequency', v)}
              options={UPDATE_FREQUENCY_OPTIONS.map(f => ({ value: f, label: f || '— None —' }))} />
            <Textarea label="Notes" value={editDeal.notes} onChange={v => setE('notes', v)} rows={3} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              <Btn variant="danger" disabled={deleting} onClick={deleteDeal}>{deleting ? 'Deleting…' : 'Delete Deal'}</Btn>
              <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="ghost" onClick={() => setShowEdit(false)}>Cancel</Btn>
                <Btn disabled={!editDeal.name?.trim() || editSaving} onClick={saveEdit}>{editSaving ? 'Saving…' : 'Save Changes'}</Btn>
              </div>
            </div>
          </Modal>
        )}

        {/* DealDetail drill-down modal */}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Deals list view
  // -------------------------------------------------------------------------
  const activeDealCount = (displayDeals || []).filter(d => d.status === 'Active').length;
  const dealCountLabel  = `${displayDeals.length} ${displayDeals.length === 1 ? 'deal' : 'deals'}${activeDealCount ? ` · ${activeDealCount} active` : ''}`;
  return (
    <div>
      <PageHeader
        icon="deals"
        title="Deals"
        subtitle={dealCountLabel}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            {user?.role === 'Admin' && (
              <>
                <Btn variant="secondary" onClick={() => setShowBulkResearch(true)}>Run research (all)</Btn>
                <Btn variant="secondary" onClick={() => setShowNaicsBulk(true)}>Classify industries</Btn>
              </>
            )}
            <Btn onClick={() => setShowNewDeal(true)}><Icon name="add" size={16} /> New Deal</Btn>
          </div>
        }
      />

      {showBulkResearch && <BulkResearchModal onClose={() => setShowBulkResearch(false)} />}
      {showNaicsBulk && <NaicsBulkModal onClose={() => setShowNaicsBulk(false)} />}

      {/* Filter bar — pill search + sort + dead toggle */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 340 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: B.textLt, display: 'flex', pointerEvents: 'none' }}>
            <Icon name="search" size={16} />
          </span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search deals…"
            aria-label="Search deals"
            style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px 9px 36px', fontSize: 13, fontFamily: 'inherit', color: B.text, background: B.surface, border: `1px solid ${B.border}`, borderRadius: 20, outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s' }}
            onFocus={e => { e.currentTarget.style.borderColor = B.accent; e.currentTarget.style.boxShadow = B.ringAccent; }}
            onBlur={e => { e.currentTarget.style.borderColor = B.border; e.currentTarget.style.boxShadow = 'none'; }}
          />
        </div>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          aria-label="Sort deals"
          style={{ padding: '9px 34px 9px 14px', border: `1px solid ${B.border}`, borderRadius: 20, fontSize: 13, color: B.text, background: B.surface, outline: 'none', fontFamily: 'inherit', appearance: 'none', cursor: 'pointer', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%234a6572' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', transition: 'border-color 0.15s, box-shadow 0.15s' }}
          onFocus={e => { e.currentTarget.style.borderColor = B.accent; e.currentTarget.style.boxShadow = B.ringAccent; }}
          onBlur={e => { e.currentTarget.style.borderColor = B.border; e.currentTarget.style.boxShadow = 'none'; }}
        >
          {DEAL_SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <Btn variant="ghost" size="sm" onClick={() => setShowDead(v => !v)}>
          {showDead ? 'Hide Dead' : 'Show Dead'}
        </Btn>
      </div>

      <Card style={{ padding: 0 }}>
        <Table
          columns={[
            { key: 'name', label: 'Deal', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: 'harvusLeadNames', label: 'Lead', render: (v, row) => (
              <span onClick={e => handleLeadClick(e, row)} style={{ cursor: 'pointer', borderBottom: `1px dashed ${B.border}` }}>
                {renderLeadCell(v)}
              </span>
            )},
            { key: 'status', label: 'Status', render: (v, row) => (
              <span onClick={e => handleStatusClick(e, row)} style={{ cursor: 'pointer' }}>
                <StatusBadge status={v} />
              </span>
            )},
            { key: 'detailCount', label: 'Capital', render: (v, row) => {
              const nr = row.nrCount || 0, re = row.reCount || 0, active = row.activeCount || 0, passive = row.passiveCount || 0;
              if (!nr && !re && !active && !passive) return <span style={{ color: B.textLt }}>—</span>;
              return (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {nr      > 0 && <Badge color={B.navy}>{nr} N/R</Badge>}
                  {re      > 0 && <Badge color={B.tealDk}>{re} R/E</Badge>}
                  {active  > 0 && <Badge color={B.green}>{active} Active</Badge>}
                  {passive > 0 && <Badge color={B.amber}>{passive} Passive</Badge>}
                </div>
              );
            }},
            { key: 'totalCost', label: 'Amount', render: (v, row) => {
              const tc = row.totalCost, cc = row.committedCapital;
              if (tc == null && cc == null) return <span style={{ color: B.textLt }}>—</span>;
              return (
                <span style={{ fontWeight: 600, color: B.navy }}>
                  {fmt.currency(tc != null ? tc : 0)}
                  {cc != null && <span style={{ fontWeight: 400, color: B.textMd }}> ({fmt.currency(cc)})</span>}
                </span>
              );
            }},
            { key: 'dealActualIRR', label: 'IRR', render: (v, row) => {
              if (v != null) return <span style={{ fontWeight: 600, color: B.green }}>{fmt.pct(v)}</span>;
              if (row.estimatedIRR != null) return <span style={{ fontStyle: 'italic', color: B.textMd }}>{fmt.pct(row.estimatedIRR)}</span>;
              return <span style={{ color: B.textLt }}>—</span>;
            }},
          ]}
          data={displayDeals}
          onRowClick={openDeal}
          virtualize
        />
      </Card>

      {/* Inline overlay */}
      {inlineEdit && (
        <div ref={inlineRef} style={{
          position: 'fixed', left: inlineEdit.x, top: inlinePos ? inlinePos.top : inlineEdit.anchorBottom + 4,
          zIndex: 500, background: B.white, border: `1px solid ${B.border}`,
          borderRadius: 8, boxShadow: B.shadowMd, minWidth: 200,
          maxHeight: `calc(100vh - 16px)`, overflowY: 'auto',
          visibility: inlinePos ? 'visible' : 'hidden',
        }}>
          {inlineEdit.type === 'status' && DEAL_STATUS_OPTIONS.filter(Boolean).map(s => (
            <div key={s} {...clickable(() => saveInlineStatus(inlineEdit.dealID, s), { role: 'option', disabled: inlineSaving })}
              style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, borderBottom: `1px solid ${B.border}`,
                background: inlineEdit.current === s ? B.surfaceMuted : 'transparent', fontWeight: inlineEdit.current === s ? 600 : 400,
                display: 'flex', alignItems: 'center', gap: 8 }}
              onMouseEnter={e => { if (inlineEdit.current !== s) e.currentTarget.style.background = B.surfaceMuted; }}
              onMouseLeave={e => { e.currentTarget.style.background = inlineEdit.current === s ? B.surfaceMuted : 'transparent'; }}>
              <StatusBadge status={s} />
            </div>
          ))}
          {inlineEdit.type === 'lead' && (
            <div style={{ padding: 12, minWidth: 260 }}>
              <HarvusLeadMultiSelect
                embedded
                users={users}
                value={inlineEdit.current}
                onChange={leads => { setInlineEdit(p => ({ ...p, current: leads })); saveInlineLeads(inlineEdit.dealID, leads); }}
              />
            </div>
          )}
        </div>
      )}

      {/* New deal modal */}
      {showNewDeal && (
        <Modal title="New Deal" onClose={() => setShowNewDeal(false)} width={520}>
          <Input label="Name *" value={newDeal.name} onChange={v => set('name', v)} required />
          <Select label="First Capital Type *" value={newDeal.detailType} onChange={v => set('detailType', v)}
            options={DETAIL_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))} />
          <HarvusLeadMultiSelect users={users} value={newDeal.harvusLeadUserIDs} onChange={v => set('harvusLeadUserIDs', v)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Est. Amount ($)" type="number" value={newDeal.estimatedAmount} onChange={v => set('estimatedAmount', v)} placeholder="e.g. 2500000" />
            <Input label="Est. IRR (%)"    type="number" value={newDeal.estimatedIRR}    onChange={v => set('estimatedIRR', v)}    placeholder="e.g. 12.5" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Est. Close Date" type="date" value={newDeal.estimatedClose} onChange={v => set('estimatedClose', v)} />
            <Input label="Deal Structure"  value={newDeal.dealStructure}  onChange={v => set('dealStructure', v)}  placeholder="e.g. Equity, Debt, JV…" />
          </div>
          <NaicsField
            value={newDeal.naicsCode}
            description={newDeal.naicsDescription}
            onChange={(code, title) => setNewDeal(p => ({ ...p, naicsCode: code, naicsDescription: title }))} />
          <Select label="Update Frequency" value={newDeal.updateFrequency} onChange={v => set('updateFrequency', v)}
            options={UPDATE_FREQUENCY_OPTIONS.map(f => ({ value: f, label: f || '— None —' }))} />
          <Textarea label="Notes" value={newDeal.notes} onChange={v => set('notes', v)} rows={2} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setShowNewDeal(false)}>Cancel</Btn>
            <Btn disabled={!newDeal.name.trim() || saving} onClick={createDeal}>{saving ? 'Creating…' : 'Create Deal'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};
